import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
// import { useNavigate } from 'react-router-dom'; // Comentado porque no se usa actualmente
import { db, storage, auth } from '../firebase';
import {
  collection,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  updateDoc,
  deleteField,
  Timestamp,
  query,
  where,
  orderBy,
  limit
} from 'firebase/firestore';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { getCloudFunctionUrl } from '../config';
// Usar fetch API nativo en lugar de axios
import './NewsletterManager.css';
import Notification from './Notification';
import NewsletterBuilder from './NewsletterBuilder';
import NewsletterEmailPreview from './NewsletterEmailPreview';
import { buildEmailHtml, DEFAULT_DESIGN } from '../utils/newsletterEmailBuilder';

const fetchWithTimeout = (url, options = {}, timeoutMs = 60000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(id));
};

const GROUPS_COLLECTION = 'newsletter_groups';
const CAMPAIGNS_COLLECTION = 'newsletter_campaigns';
const DELIVERIES_SUB = 'deliveries';
const QUEUE_SUB = 'queue';
const DELIVERY_LIST_LIMIT = 500;
const MAX_VISIBLE_TAGS_IN_ROW = 2;
const CUSTOMERS_GROUP_ID = 'clientes';
const SUBSCRIBERS_GROUP_ID = 'suscriptores';
const CONTACTOS_GROUP_ID = 'contactos';
const AUTO_GROUP_IDS = new Set([CUSTOMERS_GROUP_ID, SUBSCRIBERS_GROUP_ID, CONTACTOS_GROUP_ID]);
const UNSUBSCRIBE_PREVIEW_URL = 'https://www.priball.com/desuscribirme?email=cliente%40ejemplo.com&token=...';

function withPreviewUnsubscribeFooter(html) {
  const baseHtml = typeof html === 'string' ? html : '';
  const trimmed = baseHtml.trim();
  if (!trimmed) return '<p>No hay contenido HTML para mostrar</p>';
  if (/priball-unsubscribe-footer|priball-preview-unsubscribe-footer/i.test(trimmed)) return trimmed;

  const footer = `
<!-- priball-preview-unsubscribe-footer -->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-top:30px;border-top:1px solid #e2e8f0;">
  <tr>
    <td style="padding:24px 20px 10px;text-align:center;background:#f8fafc;">
      <p style="margin:0 0 12px;color:#334155;font-size:14px;line-height:1.6;font-family:Arial,sans-serif;">
        Si ya no querés recibir novedades y promociones de Priball, podés desuscribirte en un clic.
      </p>
      <a
        href="${UNSUBSCRIBE_PREVIEW_URL}"
        target="_blank"
        rel="noopener noreferrer"
        style="display:inline-block;padding:12px 22px;background:#ef4444;color:#ffffff;text-decoration:none;font-weight:700;font-size:14px;font-family:Arial,sans-serif;border-radius:999px;"
      >
        Desuscribirme
      </a>
      <p style="margin:14px 0 0;color:#64748b;font-size:12px;line-height:1.5;font-family:Arial,sans-serif;">
        Vista previa: el enlace final se firma automáticamente por cada destinatario.
      </p>
    </td>
  </tr>
</table>`;

  if (/<\/body>/i.test(trimmed)) {
    return trimmed.replace(/<\/body>/i, `${footer}</body>`);
  }
  return `${trimmed}${footer}`;
}

/** Destinatarios por canal (IDs virtuales en selectedRecipientIds). */
const CUSTOMER_SOURCE_SEGMENTS = [
  { id: 'clientes:ventas_fisicas', label: 'Ventas físicas', countKey: 'ventas_fisicas' },
  { id: 'clientes:pedidos', label: 'Pedidos web', countKey: 'pedidos' },
  { id: 'clientes:compras_mercadopago', label: 'Mercado Pago', countKey: 'compras_mercadopago' }
];

const RECIPIENT_SEGMENT_LABELS = {
  'clientes:ventas_fisicas': 'Ventas físicas',
  'clientes:pedidos': 'Pedidos web',
  'clientes:compras_mercadopago': 'Mercado Pago',
  [CONTACTOS_GROUP_ID]: 'Contactos (formulario web)',
  [SUBSCRIBERS_GROUP_ID]: 'Suscriptores',
  [CUSTOMERS_GROUP_ID]: 'Todos los clientes'
};

/** En campañas no listar el grupo monolítico "clientes" (usar segmentos). */
const HIDDEN_CAMPAIGN_GROUP_IDS = new Set([CUSTOMERS_GROUP_ID]);

const AUTO_GROUP_DEFAULTS = [
  { id: CUSTOMERS_GROUP_ID, name: 'Base de clientes', auto: true, emailCount: 0 },
  { id: SUBSCRIBERS_GROUP_ID, name: 'Suscriptores', auto: true, emailCount: 0 },
  { id: CONTACTOS_GROUP_ID, name: 'Contactos (formulario web)', auto: true, emailCount: 0 }
];

/** Timestamp/Date de Firestore → string para <input type="datetime-local">. */
function timestampToLocalInput(value) {
  if (!value) return '';
  try {
    const d = value.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}

function groupTitle(g) {
  const t = typeof g?.name === 'string' ? g.name.trim() : '';
  if (t) return t;
  return AUTO_GROUP_DEFAULTS.find((d) => d.id === g?.id)?.name || g?.id || '';
}

function mergeAutoGroups(fetched) {
  const byId = new Map(fetched.map((h) => [h.id, h]));
  const merged = [...fetched];
  for (const def of AUTO_GROUP_DEFAULTS) {
    if (!byId.has(def.id)) merged.push({ ...def });
  }
  const autoOrder = [CUSTOMERS_GROUP_ID, SUBSCRIBERS_GROUP_ID, CONTACTOS_GROUP_ID];
  return merged.sort((a, b) => {
    const ai = autoOrder.indexOf(a.id);
    const bi = autoOrder.indexOf(b.id);
    if (ai >= 0 && bi >= 0) return ai - bi;
    if (ai >= 0) return -1;
    if (bi >= 0) return 1;
    return groupTitle(a).localeCompare(groupTitle(b));
  });
}

const NewsletterManager = () => {
  // const navigate = useNavigate(); // Comentado porque no se usa actualmente
  
  // Estados principales
  const [activeTab, setActiveTab] = useState('campaigns');
  const [groups, setGroups] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importingHistory, setImportingHistory] = useState(null);
  const [activatingCampaignId, setActivatingCampaignId] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingGroupMembers, setLoadingGroupMembers] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);
  const [deliveryTab, setDeliveryTab] = useState('sent');
  const [deliveryRows, setDeliveryRows] = useState([]);
  const [deliverySearch, setDeliverySearch] = useState('');
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [unsubscribed, setUnsubscribed] = useState([]);
  const [loadingUnsubscribed, setLoadingUnsubscribed] = useState(false);
  const [unsubscribedSearch, setUnsubscribedSearch] = useState('');
  const [deletingUnsubscribed, setDeletingUnsubscribed] = useState(null);
  const [emailSearchQuery, setEmailSearchQuery] = useState('');
  const [emailSearchResult, setEmailSearchResult] = useState(null);
  const [emailSearching, setEmailSearching] = useState(false);
  const [emailActionBusy, setEmailActionBusy] = useState(null);

  // Estados de UI
  const [notification, setNotification] = useState({ message: '', type: 'success', show: false });
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'group', 'campaign', 'confirm'
  const [editingItem, setEditingItem] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
  
  // Estados para modal de confirmación
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null
  });

  // Estados de formularios
  const [groupForm, setGroupForm] = useState({ name: '', emails: '' });
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    group: '',
    html: '',
    active: false,
    autoSend: false,
    selectedRecipientIds: [], // 'subscribers' y/o ids de grupos
    blocks: [],
    design: { ...DEFAULT_DESIGN },
    scheduledAt: '' // datetime-local (vacío = sin programación)
  });
  // 'builder' = constructor por bloques · 'html' = editor HTML avanzado (IA / crudo)
  const [editorMode, setEditorMode] = useState('builder');
  // Destino del picker de productos: 'ai' (plantilla IA) o el id de un bloque del constructor
  const [productPickerTarget, setProductPickerTarget] = useState('ai');
  const [sendingTest, setSendingTest] = useState(false);
  const [showHtmlPreview, setShowHtmlPreview] = useState(() => {
    return (localStorage.getItem('newsletterEditorMode') || 'code') === 'preview';
  });
  const [showFullScreenEditor, setShowFullScreenEditor] = useState(false);
  const [showFullScreenPreview, setShowFullScreenPreview] = useState(false);
  const [showDetailFullScreenPreview, setShowDetailFullScreenPreview] = useState(false);
  const htmlTextareaRef = useRef(null);

  // Estados para búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Plantilla con ofertas: productos seleccionados (orden) y picker
  const [offerProductIds, setOfferProductIds] = useState([]);
  const [offerProducts, setOfferProducts] = useState([]);
  const [showOfferProductPicker, setShowOfferProductPicker] = useState(false);
  const [allOfferProducts, setAllOfferProducts] = useState([]);
  const [offerProductsLoading, setOfferProductsLoading] = useState(false);
  const [offerCategoryFilter, setOfferCategoryFilter] = useState('');
  const [offerSearch, setOfferSearch] = useState('');
  const [aiTemplateLoading, setAiTemplateLoading] = useState(false);
  const [aiTemplateError, setAiTemplateError] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');

  // Cerrar vista previa/editor a pantalla completa con Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (showDetailFullScreenPreview) setShowDetailFullScreenPreview(false);
      if (showFullScreenPreview) setShowFullScreenPreview(false);
      if (showFullScreenEditor) setShowFullScreenEditor(false);
    };
    if (showDetailFullScreenPreview || showFullScreenPreview || showFullScreenEditor) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [showDetailFullScreenPreview, showFullScreenPreview, showFullScreenEditor]);

  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ message, type, show: true });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification({ message: '', type: 'success', show: false });
  }, []);

  const callNewsletterApi = useCallback(async (functionName, body = {}, timeoutMs = 120000) => {
    const user = auth.currentUser;
    if (!user) throw new Error('Iniciá sesión para gestionar campañas.');
    const token = await user.getIdToken();
    const url = getCloudFunctionUrl(functionName);
    const res = await fetchWithTimeout(
      url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      },
      timeoutMs
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      throw new Error(data.message || data.error || `Error HTTP ${res.status}`);
    }
    return data;
  }, []);

  // Funciones de búsqueda y filtrado
  const filterEmails = useCallback((emails, search) => {
    if (!search.trim()) return emails || [];
    const searchLower = search.toLowerCase();
    return (emails || []).filter(email => 
      email.toLowerCase().includes(searchLower)
    );
  }, []);

  const loadGroupMembers = useCallback(async (groupId) => {
    if (!groupId) {
      setGroupMembers([]);
      return [];
    }
    setLoadingGroupMembers(true);
    try {
      const data = await callNewsletterApi('getNewsletterGroupMembers', { groupId });
      const emails = Array.isArray(data.emails) ? [...data.emails].sort((a, b) => a.localeCompare(b)) : [];
      setGroupMembers(emails);
      setGroups((prev) =>
        prev.map((g) => (g.id === groupId ? { ...g, emailCount: data.emailCount ?? emails.length } : g))
      );
      setSelectedGroup((prev) =>
        prev?.id === groupId ? { ...prev, emailCount: data.emailCount ?? emails.length } : prev
      );
      return emails;
    } catch (error) {
      console.error('Error cargando members del grupo (API):', error);
      try {
        const membersSnap = await getDocs(collection(db, GROUPS_COLLECTION, groupId, 'members'));
        const emails = membersSnap.docs
          .map((d) => d.data()?.email)
          .filter((email) => typeof email === 'string' && email.trim())
          .sort((a, b) => a.localeCompare(b));
        setGroupMembers(emails);
        setGroups((prev) =>
          prev.map((g) => (g.id === groupId ? { ...g, emailCount: emails.length } : g))
        );
        setSelectedGroup((prev) =>
          prev?.id === groupId ? { ...prev, emailCount: emails.length } : prev
        );
        if (emails.length > 0) {
          showNotification('Emails cargados desde Firestore (modo respaldo).', 'info');
        } else {
          showNotification('No se encontraron emails en este grupo.', 'warning');
        }
        return emails;
      } catch (fallbackError) {
        console.error('Error cargando members del grupo (Firestore):', fallbackError);
        setGroupMembers([]);
        showNotification(error.message || 'No se pudieron cargar los emails del grupo', 'error');
        return [];
      }
    } finally {
      setLoadingGroupMembers(false);
    }
  }, [callNewsletterApi, showNotification]);

  const removeEmailFromGroup = async (groupId, emailToRemove) => {
    try {
      const data = await callNewsletterApi('removeNewsletterGroupMember', {
        groupId,
        email: emailToRemove
      });
      const updatedEmails = groupMembers.filter((email) => email !== emailToRemove);
      setGroupMembers(updatedEmails);
      if (selectedGroup?.id === groupId) {
        setSelectedGroup((prev) => (prev ? { ...prev, emailCount: data.emailCount } : prev));
      }
      setGroups((prev) =>
        prev.map((g) => (g.id === groupId ? { ...g, emailCount: data.emailCount } : g))
      );
      showNotification('Email eliminado del grupo', 'success');
    } catch (error) {
      showNotification(error.message || 'Error al eliminar email', 'error');
    }
  };

  // Funciones de datos
  const fetchData = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!silent) setLoading(true);
    try {
      const [groupsResult, campaignsResult] = await Promise.allSettled([
        getDocs(collection(db, GROUPS_COLLECTION)),
        getDocs(collection(db, CAMPAIGNS_COLLECTION))
      ]);

      if (groupsResult.status === 'fulfilled') {
        const safeGroups = groupsResult.value.docs.map((d) => {
          const data = d.data() || {};
          const { emails, ...meta } = data;
          return {
            id: d.id,
            ...meta,
            name: typeof meta.name === 'string' ? meta.name : String(meta.name || ''),
            emailCount: Number.isFinite(data.emailCount)
              ? data.emailCount
              : (Array.isArray(emails) ? emails.length : 0)
          };
        });
        setGroups(mergeAutoGroups(safeGroups));
      } else {
        console.error('Error cargando grupos de newsletter:', groupsResult.reason);
        setGroups(mergeAutoGroups([]));
      }

      const campaignsList =
        campaignsResult.status === 'fulfilled'
          ? campaignsResult.value.docs.map((d) => {
              const data = d.data() || {};
              const { emailsSent, logs, ...light } = data;
              return {
                id: d.id,
                ...light,
                name: typeof light.name === 'string' ? light.name : String(light.name || '(Sin nombre)'),
                sentCount: Number.isFinite(data.sentCount)
                  ? data.sentCount
                  : Array.isArray(emailsSent)
                    ? emailsSent.length
                    : 0
              };
            })
          : [];

      if (campaignsResult.status === 'rejected') {
        console.error('Error cargando campañas de newsletter:', campaignsResult.reason);
      }

      campaignsList.sort((a, b) => {
        const timeA = a.createdAt?.toMillis?.() ?? a.createdAt?.seconds * 1000 ?? 0;
        const timeB = b.createdAt?.toMillis?.() ?? b.createdAt?.seconds * 1000 ?? 0;
        return timeB - timeA;
      });
      setCampaigns(campaignsList);

      if (groupsResult.status === 'rejected' && campaignsResult.status === 'rejected') {
        showNotification('No se pudieron cargar grupos ni campañas. Revisá permisos y sesión.', 'error');
      } else if (groupsResult.status === 'rejected') {
        showNotification('No se pudieron cargar contactos/grupos, pero sí las campañas.', 'warning');
      } else if (campaignsResult.status === 'rejected') {
        showNotification('No se pudieron cargar campañas previas, pero sí los grupos.', 'warning');
      }

      return campaignsList;
    } catch (error) {
      console.error('Error inesperado cargando newsletter:', error);
      showNotification('Error al cargar datos', 'error');
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const runHistoryImport = useCallback(
    async ({ functionName, successLabel, errorLabel, importKey }) => {
      let url;
      try {
        url = getCloudFunctionUrl(functionName);
      } catch {
        showNotification(`No hay URL configurada para ${errorLabel}`, 'error');
        return;
      }
      setImportingHistory(importKey);
      try {
        const res = await fetchWithTimeout(
          url,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fullRebuild: true })
          },
          300000
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          throw new Error(data.message || data.error || 'Error al importar');
        }
        await fetchData();
        showNotification(
          `${successLabel}: ${data.totalEmails} email(s) · ${data.newMembersAdded || 0} nuevos del historial`,
          'success'
        );
      } catch (err) {
        showNotification(err.message || `Error al importar ${errorLabel}`, 'error');
      } finally {
        setImportingHistory(null);
      }
    },
    [fetchData, showNotification]
  );

  // Persistir modo del editor (código/vista previa)
  useEffect(() => {
    localStorage.setItem('newsletterEditorMode', showHtmlPreview ? 'preview' : 'code');
  }, [showHtmlPreview]);

  useEffect(() => {
    if (!selectedGroup?.id) {
      setGroupMembers([]);
      return;
    }
    loadGroupMembers(selectedGroup.id);
  }, [selectedGroup?.id, loadGroupMembers]);

  useEffect(() => {
    if (selectedGroup) {
      setFilteredEmails(filterEmails(groupMembers, searchTerm));
    } else {
      setFilteredEmails([]);
    }
  }, [searchTerm, selectedGroup, groupMembers, filterEmails]);

  // Cargar productos para el picker de ofertas (cuando se abre el modal de productos)
  useEffect(() => {
    if (!showOfferProductPicker) return;
    if (allOfferProducts.length > 0) return;
    let cancelled = false;
    setOfferProductsLoading(true);
    const load = async () => {
      try {
        const snap = await getDocs(collection(db, 'products'));
        const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const withUrls = await Promise.all(
          raw.map(async (p) => {
            const arr = p.images || p.Images;
            const first = Array.isArray(arr) && arr.length > 0 ? arr[0] : null;
            const imgRef = first || p.image;
            let displayImageUrl = null;
            if (imgRef && typeof imgRef === 'string') {
              const s = imgRef.trim();
              if (/^https?:\/\//i.test(s)) displayImageUrl = s;
              else {
                try {
                  displayImageUrl = await getDownloadURL(storageRef(storage, s));
                } catch (_) {}
              }
            } else if (imgRef && typeof imgRef === 'object' && (imgRef.url || imgRef.src)) {
              displayImageUrl = (imgRef.url || imgRef.src).trim();
            }
            return { ...p, displayImageUrl };
          })
        );
        if (!cancelled) setAllOfferProducts(withUrls);
      } catch (e) {
        if (!cancelled) showNotification('Error al cargar productos', 'error');
      } finally {
        if (!cancelled) setOfferProductsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [showOfferProductPicker, allOfferProducts.length, showNotification]);

  const addOfferProduct = (product) => {
    if (offerProductIds.includes(product.id)) return;
    setOfferProductIds((prev) => [...prev, product.id]);
    setOfferProducts((prev) => [...prev, { ...product }]);
  };

  const removeOfferProduct = (productId) => {
    setOfferProductIds((prev) => prev.filter((id) => id !== productId));
    setOfferProducts((prev) => prev.filter((p) => p.id !== productId));
  };

  // Convierte un producto del catálogo en item de bloque "products"
  const productToBlockItem = useCallback((product) => {
    const rawPrice = product.price ?? product.Price ?? product.precio;
    let price = '';
    if (rawPrice != null && rawPrice !== '') {
      const num = Number(rawPrice);
      price = Number.isFinite(num) ? `$${num.toLocaleString('es-AR')}` : String(rawPrice);
    }
    return {
      id: product.id,
      name: product.model || product.name || 'Producto',
      image: product.displayImageUrl || '',
      price,
      url: `https://www.priball.com/producto/${product.id}`
    };
  }, []);

  // Agrega un producto a un bloque "products" del constructor (sin duplicar)
  const addProductToBlock = useCallback((blockId, product) => {
    setCampaignForm((prev) => ({
      ...prev,
      blocks: (prev.blocks || []).map((b) => {
        if (b.id !== blockId) return b;
        const items = Array.isArray(b.items) ? b.items : [];
        if (items.some((it) => it.id === product.id)) return b;
        return { ...b, items: [...items, productToBlockItem(product)] };
      })
    }));
  }, [productToBlockItem]);

  // Mantiene campaignForm.html sincronizado con los bloques mientras se usa el constructor
  useEffect(() => {
    if (modalType !== 'campaign' || editorMode !== 'builder') return;
    const generated = buildEmailHtml(campaignForm.blocks || [], campaignForm.design || {});
    setCampaignForm((prev) => (prev.html === generated ? prev : { ...prev, html: generated }));
  }, [modalType, editorMode, campaignForm.blocks, campaignForm.design]);

  const generateNewsletterTemplateWithAi = async () => {
    setAiTemplateError(null);
    setAiTemplateLoading(true);
    let url;
    try {
      url = getCloudFunctionUrl('generateNewsletterTemplate');
    } catch {
      setAiTemplateError('No hay URL configurada para el asistente de IA.');
      setAiTemplateLoading(false);
      return;
    }
    try {
      const res = await fetchWithTimeout(
        url,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt: (aiPrompt || '').trim(),
            productIds: offerProductIds
          })
        },
        60000
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || data.error || 'Error al generar plantilla');
      }
      if (data.html) {
        setCampaignForm((prev) => ({ ...prev, html: data.html }));
        setShowHtmlPreview(true);
        showNotification('Plantilla generada. Revisá la vista previa.', 'success');
      }
    } catch (err) {
      setAiTemplateError(err.message || 'Error al generar plantilla con IA');
      showNotification(err.message || 'Error al generar plantilla', 'error');
    } finally {
      setAiTemplateLoading(false);
    }
  };

  const copyHtmlToClipboard = useCallback(() => {
    const html = campaignForm.html?.trim() || '';
    if (!html) {
      showNotification('No hay contenido HTML para copiar', 'error');
      return;
    }
    navigator.clipboard.writeText(html).then(() => {
      showNotification('HTML copiado al portapapeles', 'success');
    }).catch(() => {
      showNotification('No se pudo copiar el HTML', 'error');
    });
  }, [campaignForm.html, showNotification]);

  const selectAllHtml = useCallback(() => {
    if (!showHtmlPreview && htmlTextareaRef.current) {
      htmlTextareaRef.current.focus();
      htmlTextareaRef.current.select();
      showNotification('Todo el contenido seleccionado', 'info');
    } else if (showHtmlPreview) {
      setShowHtmlPreview(false);
      setTimeout(() => {
        if (htmlTextareaRef.current) {
          htmlTextareaRef.current.focus();
          htmlTextareaRef.current.select();
          showNotification('Todo el contenido seleccionado', 'info');
        }
      }, 100);
    }
  }, [showHtmlPreview, showNotification]);

  // Función para mostrar modal de confirmación
  const showConfirm = (title, message, onConfirm) => {
    setConfirmModal({
      show: true,
      title,
      message,
      onConfirm,
      onCancel: () => setConfirmModal({ ...confirmModal, show: false })
    });
  };

  const handleConfirm = () => {
    if (confirmModal.onConfirm) {
      confirmModal.onConfirm();
    }
    setConfirmModal({ ...confirmModal, show: false });
  };

  const handleCancel = () => {
    setConfirmModal({ ...confirmModal, show: false });
  };

  const importCustomersHistory = useCallback(() => {
    showConfirm(
      'Importar clientes del historial',
      'Lee ventas físicas, pedidos y Mercado Pago en lotes (solo para emails antiguos). Puede tardar varios minutos. ¿Continuar?',
      () =>
        runHistoryImport({
          functionName: 'syncCustomersNewsletterGroup',
          successLabel: 'Base de clientes',
          errorLabel: 'la base de clientes',
          importKey: 'customers'
        })
    );
  }, [runHistoryImport]);

  const importSubscribersHistory = useCallback(() => {
    showConfirm(
      'Importar suscriptores del historial',
      'Lee la colección Subscriptions en lotes (solo para suscripciones antiguas). ¿Continuar?',
      () =>
        runHistoryImport({
          functionName: 'syncSubscribersNewsletterGroup',
          successLabel: 'Suscriptores',
          errorLabel: 'los suscriptores',
          importKey: 'subscribers'
        })
    );
  }, [runHistoryImport]);

  const importContactsHistory = useCallback(() => {
    showConfirm(
      'Importar contactos del historial',
      'Lee la colección contacts en lotes (solo para consultas antiguas del formulario). ¿Continuar?',
      () =>
        runHistoryImport({
          functionName: 'syncContactsNewsletterGroup',
          successLabel: 'Contactos',
          errorLabel: 'los contactos',
          importKey: 'contacts'
        })
    );
  }, [runHistoryImport]);

  const getCustomerSourceCount = useCallback(
    (countKey) => {
      const g = groups.find((gr) => gr.id === CUSTOMERS_GROUP_ID);
      return Number(g?.sourceCounts?.[countKey]) || 0;
    },
    [groups]
  );

  const getGroupEmailCount = useCallback(
    (groupId) => {
      const g = groups.find((gr) => gr.id === groupId);
      return Number(g?.emailCount) || 0;
    },
    [groups]
  );

  const toggleRecipientId = useCallback((recipientId) => {
    setCampaignForm((prev) => {
      const current = prev.selectedRecipientIds || [];
      const next = current.includes(recipientId)
        ? current.filter((id) => id !== recipientId)
        : [...current, recipientId];
      return { ...prev, selectedRecipientIds: next };
    });
  }, []);

  // Funciones de grupos
  const openGroupModal = async (group = null) => {
    setEditingItem(group);
    setModalType('group');
    setShowModal(true);
    if (group?.id) {
      setGroupForm({ name: group.name || '', emails: 'Cargando…' });
      const emails = await loadGroupMembers(group.id);
      setGroupForm({ name: group.name || '', emails: emails.join('\n') });
    } else {
      setGroupForm({ name: '', emails: '' });
    }
  };

  const saveGroup = async () => {
    const name = groupForm.name.trim();
    const emails = groupForm.emails
      .split(/[\n,;]/)
      .map(e => e.trim())
      .filter(e => e && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));

    if (!name || emails.length === 0) {
      showNotification('Completa nombre y emails válidos', 'error');
      return;
    }

    try {
      let id = editingItem?.id || name.toLowerCase().replace(/[^a-z0-9]/g, '-');
      if (!editingItem && AUTO_GROUP_IDS.has(id)) {
        showNotification('Ese identificador está reservado para un grupo automático.', 'error');
        return;
      }
      if (AUTO_GROUP_IDS.has(editingItem?.id) || editingItem?.auto) {
        showNotification('Los grupos automáticos no se editan manualmente. Usá los botones de sincronización en la pestaña Grupos.', 'error');
        return;
      }
      setSavingGroup(true);
      const data = await callNewsletterApi('saveNewsletterGroup', {
        groupId: editingItem?.id || id,
        name,
        emails
      });
      setShowModal(false);
      setEditingItem(null);
      setShowHtmlPreview(false);
      setShowFullScreenPreview(false);
      await fetchData();
      if (selectedGroup?.id === data.groupId) {
        await loadGroupMembers(data.groupId);
      }
      showNotification(editingItem ? 'Grupo actualizado' : 'Grupo creado', 'success');
    } catch (error) {
      showNotification(error.message || 'Error al guardar grupo', 'error');
    } finally {
      setSavingGroup(false);
    }
  };

  const deleteGroup = async (id) => {
    if (AUTO_GROUP_IDS.has(id)) {
      showNotification('Los grupos automáticos no se pueden eliminar. Se mantienen sincronizados desde el sistema.', 'error');
      return;
    }
    const confirmDelete = async () => {
      try {
        await callNewsletterApi('deleteNewsletterGroup', { groupId: id });
        setSelectedGroup(null);
        setGroupMembers([]);
        await fetchData();
        showNotification('Grupo eliminado', 'success');
      } catch (error) {
        showNotification(error.message || 'Error al eliminar grupo', 'error');
      }
    };

    showConfirm(
      'Eliminar Grupo',
      '¿Estás seguro de que deseas eliminar este grupo? Esta acción no se puede deshacer.',
      confirmDelete
    );
  };

  // Desuscriptos
  const loadUnsubscribed = useCallback(async () => {
    setLoadingUnsubscribed(true);
    try {
      const data = await callNewsletterApi('listNewsletterUnsubscribed', {});
      setUnsubscribed(Array.isArray(data.items) ? data.items : []);
    } catch (error) {
      showNotification(error.message || 'Error al cargar desuscriptos', 'error');
      setUnsubscribed([]);
    } finally {
      setLoadingUnsubscribed(false);
    }
  }, [callNewsletterApi, showNotification]);

  const deleteUnsubscribed = (item) => {
    if (!item?.email) return;
    const confirmDelete = async () => {
      setDeletingUnsubscribed(item.id || item.email);
      try {
        await callNewsletterApi('deleteNewsletterUnsubscribed', { id: item.id, email: item.email });
        setUnsubscribed((prev) => prev.filter((u) => (u.id || u.email) !== (item.id || item.email)));
        showNotification('Contacto eliminado de la lista de desuscriptos', 'success');
      } catch (error) {
        showNotification(error.message || 'Error al eliminar el contacto', 'error');
      } finally {
        setDeletingUnsubscribed(null);
      }
    };
    showConfirm(
      'Eliminar desuscripto',
      `¿Eliminar a "${item.email}" del registro de desuscriptos? Dejará de estar en la lista de bloqueo: si vuelve a algún grupo, podría recibir emails de nuevo.`,
      confirmDelete
    );
  };

  useEffect(() => {
    if (activeTab === 'unsubscribed') {
      loadUnsubscribed();
    }
  }, [activeTab, loadUnsubscribed]);

  // Búsqueda en vivo por prefijo (lo de la izquierda del @ o el email completo). Sin botón, con debounce.
  useEffect(() => {
    const query = emailSearchQuery.trim();
    if (query.length < 2) {
      setEmailSearchResult(null);
      setEmailSearching(false);
      return undefined;
    }
    let cancelled = false;
    setEmailSearching(true);
    const timer = setTimeout(async () => {
      try {
        const data = await callNewsletterApi('findNewsletterEmail', { query });
        if (!cancelled) {
          setEmailSearchResult({
            query: data.query || query,
            matches: Array.isArray(data.matches) ? data.matches : [],
            truncated: data.truncated === true
          });
        }
      } catch (error) {
        if (!cancelled) showNotification(error.message || 'Error al buscar', 'error');
      } finally {
        if (!cancelled) setEmailSearching(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [emailSearchQuery, callNewsletterApi, showNotification]);

  // Quita un email de un grupo y actualiza la coincidencia (sin confirmación).
  const removeFoundEmailFromGroup = async (groupId, email) => {
    setEmailActionBusy(`${groupId}:${email}`);
    try {
      await callNewsletterApi('removeNewsletterGroupMember', { groupId, email });
      setEmailSearchResult((prev) => {
        if (!prev) return prev;
        const matches = prev.matches
          .map((m) => (m.email === email ? { ...m, groups: m.groups.filter((g) => g.id !== groupId) } : m))
          .filter((m) => m.groups.length > 0);
        return { ...prev, matches };
      });
      fetchData({ silent: true });
    } catch (error) {
      showNotification(error.message || 'Error al quitar el email', 'error');
    } finally {
      setEmailActionBusy(null);
    }
  };

  // Elimina un email de todos los grupos y de suscripciones (sin confirmación).
  const purgeEmailEverywhere = async (email) => {
    setEmailActionBusy(`all:${email}`);
    try {
      const data = await callNewsletterApi('purgeNewsletterEmail', { email });
      const count = (data.removedGroups || []).length;
      setEmailSearchResult((prev) => (prev ? { ...prev, matches: prev.matches.filter((m) => m.email !== email) } : prev));
      fetchData({ silent: true });
      showNotification(
        `"${email}" eliminado de ${count} grupo(s)${data.subsDeleted ? ` y ${data.subsDeleted} suscripción(es)` : ''}.`,
        'success'
      );
    } catch (error) {
      showNotification(error.message || 'Error al eliminar el email', 'error');
    } finally {
      setEmailActionBusy(null);
    }
  };

  // Funciones de campañas
  const closeCampaignModal = useCallback(() => {
    const hasContent = (campaignForm.name && campaignForm.name.trim()) || (campaignForm.html && campaignForm.html.trim());
    if (modalType === 'campaign' && hasContent) {
      if (window.confirm('¿Salir sin guardar? Los cambios en la campaña se perderán.')) {
        setShowModal(false);
        setShowHtmlPreview(false);
        setShowFullScreenPreview(false);
      }
    } else {
      setShowModal(false);
      setShowHtmlPreview(false);
      setShowFullScreenPreview(false);
    }
  }, [modalType, campaignForm.name, campaignForm.html]);

  const openCampaignModal = (campaign = null) => {
    setEditingItem(campaign);
    setOfferProductIds([]);
    setOfferProducts([]);
    setAiTemplateError(null);
    setAiPrompt('');
    // Soporte múltiple: selectedRecipientIds; compatibilidad con campañas antiguas (group + recipientType).
    // Los antiguos ids especiales 'subscribers' y 'customers' se migran a los grupos auto correspondientes.
    const legacyToGroup = (id) => {
      if (id === 'customers') return CUSTOMERS_GROUP_ID;
      if (id === 'subscribers') return SUBSCRIBERS_GROUP_ID;
      if (id === 'contacts' || id === 'contactos') return CONTACTOS_GROUP_ID;
      return id;
    };
    let selectedRecipientIds = [];
    if (campaign?.selectedRecipientIds && Array.isArray(campaign.selectedRecipientIds) && campaign.selectedRecipientIds.length > 0) {
      selectedRecipientIds = [...campaign.selectedRecipientIds];
    } else if (campaign) {
      if (campaign.recipientType === 'subscribers') selectedRecipientIds = [SUBSCRIBERS_GROUP_ID];
      else if (campaign.recipientType === 'customers') selectedRecipientIds = [CUSTOMERS_GROUP_ID];
      else if (campaign.group) selectedRecipientIds = [campaign.group];
    }
    selectedRecipientIds = selectedRecipientIds.map(legacyToGroup);
    const hasBlocks = Array.isArray(campaign?.blocks) && campaign.blocks.length > 0;
    const design = campaign?.design && typeof campaign.design === 'object'
      ? { ...DEFAULT_DESIGN, ...campaign.design }
      : { ...DEFAULT_DESIGN };
    setCampaignForm({
      name: campaign?.name || '',
      group: campaign?.group || '',
      html: campaign?.html || '',
      active: campaign?.active || false,
      autoSend: campaign?.autoSend || false,
      recipientType: campaign?.recipientType || (campaign?.group ? 'group' : 'multiple'),
      selectedRecipientIds,
      blocks: hasBlocks ? campaign.blocks : [],
      design,
      scheduledAt: timestampToLocalInput(campaign?.scheduledAt)
    });
    // Campañas con bloques abren el constructor; las que solo tienen HTML, el editor avanzado
    setEditorMode(hasBlocks || !campaign ? 'builder' : 'html');
    setShowHtmlPreview(false); // Reset preview state
    setModalType('campaign');
    setShowModal(true);
  };

  const saveCampaign = async ({ asDraft = false } = {}) => {
    const { name } = campaignForm;
    const usingBuilder = editorMode === 'builder';
    const blocks = Array.isArray(campaignForm.blocks) ? campaignForm.blocks : [];
    const design = campaignForm.design || { ...DEFAULT_DESIGN };
    // En modo constructor el HTML siempre se deriva de los bloques (fuente de verdad)
    const html = usingBuilder ? buildEmailHtml(blocks, design) : campaignForm.html;
    const rawIds = campaignForm.selectedRecipientIds || [];
    const legacyToGroup = (id) => {
      if (id === 'customers') return CUSTOMERS_GROUP_ID;
      if (id === 'subscribers') return SUBSCRIBERS_GROUP_ID;
      if (id === 'contacts' || id === 'contactos') return CONTACTOS_GROUP_ID;
      return id;
    };
    const selectedRecipientIds = rawIds.map(legacyToGroup);
    if (!name.trim() || !html.trim() || selectedRecipientIds.length === 0) {
      showNotification('Completa nombre, contenido y selecciona al menos un grupo de destinatarios', 'error');
      return;
    }

    // Programación opcional: datetime-local → Timestamp
    const scheduledRaw = (campaignForm.scheduledAt || '').trim();
    let scheduledDate = null;
    if (scheduledRaw) {
      const d = new Date(scheduledRaw);
      if (!Number.isNaN(d.getTime())) scheduledDate = d;
    }
    const willSchedule = !asDraft && scheduledDate;

    try {
      const payload = {
        name: name.trim(),
        html,
        active: false,
        autoSend: false,
        finished: false,
        queueReady: false,
        selectedRecipientIds,
        blocks: usingBuilder ? blocks : [],
        design: usingBuilder ? design : (campaignForm.design || null),
        status: asDraft ? 'draft' : willSchedule ? 'scheduled' : 'ready'
      };
      if (willSchedule) {
        payload.scheduledAt = Timestamp.fromDate(scheduledDate);
      }
      // Compatibilidad: mantener group/recipientType si solo hay un destinatario
      if (selectedRecipientIds.length === 1) {
        payload.recipientType = 'group';
        payload.group = selectedRecipientIds[0];
      } else {
        payload.recipientType = 'multiple';
        payload.group = '';
      }

      if (editingItem) {
        const updatePayload = { ...payload };
        if (!willSchedule) updatePayload.scheduledAt = deleteField();
        await updateDoc(doc(db, CAMPAIGNS_COLLECTION, editingItem.id), updatePayload);
      } else {
        const docRef = doc(collection(db, CAMPAIGNS_COLLECTION));
        await setDoc(docRef, {
          ...payload,
          sentCount: 0,
          createdAt: Timestamp.now()
        });
      }
      
      setShowModal(false);
      setEditingItem(null);
      setShowHtmlPreview(false);
      setShowFullScreenPreview(false);
      await fetchData();
      showNotification(
        asDraft
          ? 'Borrador guardado'
          : editingItem
            ? 'Campaña actualizada'
            : 'Campaña creada',
        'success'
      );
    } catch (error) {
      showNotification('Error al guardar campaña', 'error');
    }
  };

  // Envía un correo de prueba con el contenido actual del editor
  const sendTestEmail = async () => {
    const email = window.prompt('Ingresá el email para recibir la prueba:');
    if (email == null) return;
    const trimmed = email.trim();
    if (!trimmed || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      showNotification('Email de prueba inválido.', 'error');
      return;
    }
    const html = (campaignForm.html || '').trim();
    if (!html) {
      showNotification('No hay contenido para enviar.', 'error');
      return;
    }
    setSendingTest(true);
    try {
      await callNewsletterApi(
        'sendNewsletterTest',
        { campaignId: editingItem?.id || '', html, email: trimmed },
        60000
      );
      showNotification(`Prueba enviada a ${trimmed}`, 'success');
    } catch (error) {
      showNotification(error.message || 'Error al enviar la prueba', 'error');
    } finally {
      setSendingTest(false);
    }
  };

  // Clona una campaña como borrador (resetea contadores y estado de envío)
  const duplicateCampaign = async (campaign) => {
    try {
      const docRef = doc(collection(db, CAMPAIGNS_COLLECTION));
      await setDoc(docRef, {
        name: `${campaign.name || 'Campaña'} (copia)`,
        html: campaign.html || '',
        blocks: Array.isArray(campaign.blocks) ? campaign.blocks : [],
        design: campaign.design || null,
        selectedRecipientIds: Array.isArray(campaign.selectedRecipientIds) ? campaign.selectedRecipientIds : [],
        recipientType: campaign.recipientType || 'multiple',
        group: campaign.group || '',
        status: 'draft',
        active: false,
        autoSend: false,
        finished: false,
        queueReady: false,
        sentCount: 0,
        successCount: 0,
        errorCount: 0,
        recipientTotal: 0,
        createdAt: Timestamp.now()
      });
      await fetchData();
      showNotification('Campaña duplicada como borrador', 'success');
    } catch (error) {
      console.error('Error al duplicar campaña:', error);
      showNotification('Error al duplicar campaña', 'error');
    }
  };

  const deleteCampaign = async (id) => {
    const confirmDelete = async () => {
      try {
        await callNewsletterApi('deleteNewsletterCampaign', { campaignId: id });
        setSelectedCampaign(null);
        setSelectedCampaignIds((prev) => prev.filter((x) => x !== id));
        await fetchData();
        showNotification('Campaña eliminada', 'success');
      } catch (error) {
        showNotification(error.message || 'Error al eliminar campaña', 'error');
      }
    };

    showConfirm(
      'Eliminar Campaña',
      '¿Estás seguro de que deseas eliminar esta campaña? Esta acción no se puede deshacer.',
      confirmDelete
    );
  };

  const toggleCampaignSelection = (id) => {
    setSelectedCampaignIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAllCampaigns = () => {
    setSelectedCampaignIds(campaigns.map(c => c.id));
  };

  const clearCampaignSelection = () => {
    setSelectedCampaignIds([]);
  };

  const deleteCampaignsBulk = () => {
    const ids = selectedCampaignIds;
    const confirmBulkDelete = async () => {
      try {
        for (const id of ids) {
          await callNewsletterApi('deleteNewsletterCampaign', { campaignId: id });
        }
        setSelectedCampaign(null);
        setSelectedCampaignIds([]);
        await fetchData();
        showNotification(`${ids.length} campaña(s) eliminada(s)`, 'success');
      } catch (error) {
        showNotification(error.message || 'Error al eliminar campañas', 'error');
      }
    };
    showConfirm(
      'Eliminar campañas seleccionadas',
      `¿Eliminar ${ids.length} campaña(s)? Esta acción no se puede deshacer.`,
      confirmBulkDelete
    );
  };

  // Activar: arma cola en servidor y comienza envío. Pausar: detiene sin borrar cola.
  const toggleCampaign = async (campaign) => {
    const willBeActive = !campaign.active;
    if (willBeActive) {
      const total = getRecipientTotal(campaign);
      if (total === 0) {
        showNotification('La campaña no tiene destinatarios válidos.', 'error');
        return;
      }
      setActivatingCampaignId(campaign.id);
      showNotification(
        'Campaña en envío en el servidor. Podés cerrar esta página; no hace falta dejar el panel abierto.',
        'success'
      );
      callNewsletterApi('prepareNewsletterCampaign', { campaignId: campaign.id }, 300000)
        .then(async (data) => {
          await fetchData();
          if (data.completed) {
            showNotification(
              `Campaña completada (${Number(data.sentNow) || 0} enviados en esta activación).`,
              'success'
            );
          }
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return;
          showNotification(error.message || 'Error al activar campaña', 'error');
        })
        .finally(() => setActivatingCampaignId(null));
      return;
    }
    try {
      await callNewsletterApi('pauseNewsletterCampaign', { campaignId: campaign.id });
      await fetchData();
      showNotification('Campaña pausada.', 'info');
    } catch (error) {
      showNotification(error.message || 'Error al pausar campaña', 'error');
    }
  };

  // Utilidades: total de destinatarios. Todos los destinatarios son grupos (manuales o auto).
  // Migración: los ids antiguos 'subscribers' y 'customers' se mapean a los grupos auto.
  const resolveRecipientIds = (campaign) => {
    const legacyToGroup = (id) => {
      if (id === 'customers') return CUSTOMERS_GROUP_ID;
      if (id === 'subscribers') return SUBSCRIBERS_GROUP_ID;
      if (id === 'contacts' || id === 'contactos') return CONTACTOS_GROUP_ID;
      return id;
    };
    let ids;
    if (campaign.selectedRecipientIds && campaign.selectedRecipientIds.length > 0) {
      ids = campaign.selectedRecipientIds;
    } else if (campaign.recipientType === 'subscribers') {
      ids = [SUBSCRIBERS_GROUP_ID];
    } else if (campaign.recipientType === 'customers') {
      ids = [CUSTOMERS_GROUP_ID];
    } else if (campaign.group) {
      ids = [campaign.group];
    } else {
      ids = [];
    }
    return ids.map(legacyToGroup);
  };

  /** Estimación rápida (suma emailCount; el servidor deduplica al activar). */
  const getRecipientTotal = (campaign) => {
    const ids = resolveRecipientIds(campaign);
    return ids.reduce((sum, recipientId) => {
      const segment = CUSTOMER_SOURCE_SEGMENTS.find((s) => s.id === recipientId);
      if (segment) return sum + getCustomerSourceCount(segment.countKey);
      if (recipientId === CUSTOMERS_GROUP_ID) {
        const g = groups.find((gr) => gr.id === CUSTOMERS_GROUP_ID);
        return sum + (Number(g?.emailCount) || 0);
      }
      return sum + getGroupEmailCount(recipientId);
    }, 0);
  };

  const getRecipientSummary = (campaign) => {
    const tags = getRecipientTags(campaign);
    if (tags.length === 0) return 'Sin destinatarios';
    return tags.map(t => `${t.label} (${t.count})`).join(' + ');
  };

  /** Devuelve array de { label, count } para mostrar como tags/chips */
  const getRecipientTags = (campaign) => {
    const ids = resolveRecipientIds(campaign);
    const result = [];
    ids.forEach((recipientId) => {
      const segment = CUSTOMER_SOURCE_SEGMENTS.find((s) => s.id === recipientId);
      if (segment) {
        result.push({ label: segment.label, count: getCustomerSourceCount(segment.countKey) });
        return;
      }
      const label = RECIPIENT_SEGMENT_LABELS[recipientId];
      const g = groups.find((gr) => gr.id === recipientId);
      if (label || g) {
        result.push({
          label: label || g.name,
          count:
            recipientId === CUSTOMERS_GROUP_ID
              ? Number(g?.emailCount) || 0
              : getGroupEmailCount(recipientId)
        });
      }
    });
    return result;
  };

  const getCampaignStats = (campaign) => {
    const total =
      Number(campaign.recipientTotal) > 0 ? Number(campaign.recipientTotal) : getRecipientTotal(campaign);
    const sent = Number.isFinite(campaign.successCount)
      ? campaign.successCount
      : Number.isFinite(campaign.sentCount)
        ? campaign.sentCount
        : Array.isArray(campaign.emailsSent)
          ? campaign.emailsSent.length
          : 0;
    const errors = Number(campaign.errorCount) || 0;
    const pending = Math.max(0, total - sent - errors);
    return { total, sent, errors, pending };
  };

  const getProgress = (campaign) => {
    const { total, sent } = getCampaignStats(campaign);
    return {
      sent,
      total,
      percent: total ? Math.min(100, Math.round((sent / total) * 100)) : 0
    };
  };

  const formatDeliveryDate = (value) => {
    if (!value) return '—';
    try {
      const d = value.toDate ? value.toDate() : new Date(value);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return '—';
    }
  };

  const loadCampaignDeliveries = useCallback(async (campaignId, tab) => {
    if (!campaignId) {
      setDeliveryRows([]);
      return;
    }
    setLoadingDeliveries(true);
    try {
      if (tab === 'pending') {
        const q = query(
          collection(db, CAMPAIGNS_COLLECTION, campaignId, QUEUE_SUB),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'asc'),
          limit(DELIVERY_LIST_LIMIT)
        );
        const snap = await getDocs(q);
        setDeliveryRows(
          snap.docs.map((d) => ({
            id: d.id,
            email: d.data().email || '',
            status: 'pending',
            sentAt: d.data().createdAt || null,
            error: d.data().lastError || ''
          }))
        );
        return;
      }
      const status = tab === 'error' ? 'error' : 'sent';
      const q = query(
        collection(db, CAMPAIGNS_COLLECTION, campaignId, DELIVERIES_SUB),
        where('status', '==', status),
        orderBy('sentAt', 'desc'),
        limit(DELIVERY_LIST_LIMIT)
      );
      const snap = await getDocs(q);
      setDeliveryRows(
        snap.docs.map((d) => ({
          id: d.id,
          email: d.data().email || '',
          status: d.data().status || status,
          sentAt: d.data().sentAt || null,
          error: d.data().error || ''
        }))
      );
    } catch (error) {
      console.error('Error cargando entregas:', error);
      setDeliveryRows([]);
    } finally {
      setLoadingDeliveries(false);
    }
  }, []);

  const exportDeliveriesCsv = useCallback(() => {
    const term = deliverySearch.trim().toLowerCase();
    const rows = deliveryRows.filter((r) => !term || r.email.toLowerCase().includes(term));
    if (rows.length === 0) {
      showNotification('No hay filas para exportar', 'warning');
      return;
    }
    const header = 'email,estado,fecha,error\n';
    const body = rows
      .map((r) => {
        const date = formatDeliveryDate(r.sentAt);
        const err = (r.error || '').replace(/"/g, '""');
        return `"${r.email}","${r.status}","${date}","${err}"`;
      })
      .join('\n');
    const blob = new Blob([header + body], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campania-${selectedCampaign?.name || 'newsletter'}-${deliveryTab}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showNotification('CSV descargado', 'success');
  }, [deliveryRows, deliverySearch, deliveryTab, selectedCampaign?.name, showNotification]);

  useEffect(() => {
    if (!selectedCampaign?.id) {
      setDeliveryRows([]);
      return undefined;
    }
    loadCampaignDeliveries(selectedCampaign.id, deliveryTab);
    if (!selectedCampaign.active) return undefined;
    const id = setInterval(async () => {
      loadCampaignDeliveries(selectedCampaign.id, deliveryTab);
      const list = await fetchData({ silent: true });
      if (list) {
        const fresh = list.find((c) => c.id === selectedCampaign.id);
        if (fresh) setSelectedCampaign(fresh);
      }
    }, 15000);
    return () => clearInterval(id);
  }, [selectedCampaign?.id, selectedCampaign?.active, deliveryTab, loadCampaignDeliveries, fetchData]);

  const filteredDeliveryRows = useMemo(() => {
    const term = deliverySearch.trim().toLowerCase();
    if (!term) return deliveryRows;
    return deliveryRows.filter((r) => r.email.toLowerCase().includes(term));
  }, [deliveryRows, deliverySearch]);

  const getStats = () => {
    const totalCampaigns = campaigns.length;
    const activeCampaigns = campaigns.filter(c => c.active).length;
    const totalGroups = groups.length;
    const totalEmails = groups.reduce((sum, group) => sum + (group.emailCount ?? 0), 0);
    
    return { totalCampaigns, activeCampaigns, totalGroups, totalEmails };
  };

  const stats = getStats();

  // Componentes de UI: Tooltip con portal (position:fixed) para que nunca quede cortado por overflow
  const Tooltip = ({ text, children, position = 'top' }) => {
    const [show, setShow] = useState(false);
    const [coords, setCoords] = useState({ top: -9999, left: -9999 });
    const triggerRef = useRef(null);
    const tooltipRef = useRef(null);

    const updatePosition = useCallback(() => {
      if (!triggerRef.current || !tooltipRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const tip = tooltipRef.current.getBoundingClientRect();
      const gap = 6;
      const padding = 8;
      let top = 0;
      let left = Math.max(padding, Math.min(rect.left + rect.width / 2 - tip.width / 2, document.documentElement.clientWidth - tip.width - padding));
      if (position === 'top') {
        top = Math.max(padding, rect.top - tip.height - gap);
      } else if (position === 'bottom') {
        top = rect.bottom + gap;
      } else {
        top = Math.max(padding, rect.top + rect.height / 2 - tip.height / 2);
        if (position === 'left') left = rect.left - tip.width - gap;
        else left = rect.right + gap;
      }
      setCoords({ top, left });
    }, [position]);

    useEffect(() => {
      if (!show || !triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({ top: rect.top - 8, left: rect.left });
      const raf = requestAnimationFrame(() => updatePosition());
      const onScrollOrResize = () => updatePosition();
      window.addEventListener('scroll', onScrollOrResize, true);
      window.addEventListener('resize', onScrollOrResize);
      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener('scroll', onScrollOrResize, true);
        window.removeEventListener('resize', onScrollOrResize);
      };
    }, [show, updatePosition]);

    return (
      <>
        <div
          ref={triggerRef}
          className="newsletter-tooltip-container"
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
        >
          {children}
        </div>
        {show && text && createPortal(
          <div
            ref={tooltipRef}
            className={`newsletter-tooltip newsletter-tooltip-fixed newsletter-tooltip-${position}`}
            style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 10000 }}
            role="tooltip"
          >
            {text}
          </div>,
          document.body
        )}
      </>
    );
  };

  const Badge = ({ children, variant = 'secondary' }) => (
    <span className={`newsletter-badge ${variant}`}>
      {children}
    </span>
  );

  // Renderizado de pestañas
  const renderCampaigns = () => (
    <div className="newsletter-tab-content">
      <div className="newsletter-tab-header">
        <h2>Campañas de Email</h2>
        <div className="newsletter-tab-header-actions">
          {selectedCampaignIds.length > 0 && (
            <div className="newsletter-bulk-actions">
              <span className="newsletter-bulk-count">{selectedCampaignIds.length} seleccionada(s)</span>
              <Tooltip text="Eliminar las campañas seleccionadas (acción irreversible)">
                <button
                  type="button"
                  className="newsletter-btn newsletter-btn-danger newsletter-btn-small"
                  onClick={deleteCampaignsBulk}
                >
                  🗑️ Eliminar
                </button>
              </Tooltip>
              <Tooltip text="Quitar la selección de todas las campañas">
                <button
                  type="button"
                  className="newsletter-btn newsletter-btn-secondary newsletter-btn-small"
                  onClick={clearCampaignSelection}
                >
                  Desmarcar
                </button>
              </Tooltip>
            </div>
          )}
          {campaigns.length > 0 && selectedCampaignIds.length === 0 && (
            <Tooltip text="Marcar todas las campañas para eliminar varias a la vez">
              <button
                type="button"
                className="newsletter-btn newsletter-btn-secondary newsletter-btn-small"
                onClick={selectAllCampaigns}
              >
                Seleccionar todas
              </button>
            </Tooltip>
          )}
          <Tooltip text="Crear nueva campaña">
            <button 
              className="newsletter-btn newsletter-btn-primary"
              onClick={() => openCampaignModal()}
            >
              <span>+</span> Nueva Campaña
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="newsletter-campaigns-list">
        {campaigns.length === 0 && (
          <div className="newsletter-empty-state">
            <p>No hay campañas todavía. Creá una con <strong>+ Nueva Campaña</strong>.</p>
          </div>
        )}
        {campaigns.map(campaign => {
          const progress = getProgress(campaign);
          const isSelected = selectedCampaignIds.includes(campaign.id);
          return (
            <div 
              key={campaign.id} 
              className={`newsletter-campaign-card newsletter-campaign-row ${campaign.active ? 'newsletter-campaign-card-active' : ''} ${selectedCampaign?.id === campaign.id ? 'selected' : ''} ${isSelected ? 'newsletter-campaign-row-selected' : ''}`}
              onClick={() => setSelectedCampaign(campaign)}
            >
              <div className="newsletter-campaign-row-inner">
                <div className="newsletter-campaign-row-cell newsletter-campaign-row-title-cell">
                  <Tooltip text="Clic para ver detalle de la campaña">
                    <h3 className="newsletter-campaign-row-title">{campaign.name}</h3>
                  </Tooltip>
                </div>
                <div className="newsletter-campaign-row-cell newsletter-campaign-row-badges-cell">
                  {campaign.finished ? (
                    <Tooltip text="Todos los emails fueron enviados; la campaña está finalizada">
                      <span className="newsletter-cell-content">
                        <Badge variant="info">Finalizada</Badge>
                      </span>
                    </Tooltip>
                  ) : campaign.status === 'scheduled' && campaign.scheduledAt && !campaign.active ? (
                    <Tooltip text={`Programada para ${formatDeliveryDate(campaign.scheduledAt)}`}>
                      <span className="newsletter-cell-content">
                        <Badge variant="info">⏰ Programada</Badge>
                      </span>
                    </Tooltip>
                  ) : campaign.status === 'draft' && !campaign.active ? (
                    <Tooltip text="Borrador: todavía no se envió. Editá y publicá cuando esté lista.">
                      <span className="newsletter-cell-content">
                        <Badge variant="warning">Borrador</Badge>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip text={campaign.active ? "Campaña activa: se están enviando emails" : "Campaña pausada: no se envían emails"}>
                      <span className="newsletter-cell-content">
                        <Badge variant={campaign.active ? 'success' : 'secondary'}>
                          {campaign.active ? 'Activa' : 'Pausada'}
                        </Badge>
                      </span>
                    </Tooltip>
                  )}
                </div>
                <div className="newsletter-campaign-row-cell newsletter-campaign-row-tags-cell">
                  <Tooltip text={`Destinatarios: ${getRecipientSummary(campaign)}`}>
                    <div className="newsletter-recipient-tags newsletter-recipient-tags-inline">
                      {(() => {
                        const allTags = getRecipientTags(campaign);
                        const visible = allTags.slice(0, MAX_VISIBLE_TAGS_IN_ROW);
                        const rest = allTags.length - MAX_VISIBLE_TAGS_IN_ROW;
                        return (
                          <>
                            {visible.map((tag, idx) => (
                              <span key={idx} className="newsletter-tag">
                                <span className="newsletter-tag-label">{tag.label}</span> <span className="newsletter-tag-count">({tag.count})</span>
                              </span>
                            ))}
                            {rest > 0 && (
                              <span className="newsletter-tag newsletter-tag-more">+{rest} más</span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </Tooltip>
                </div>
                <div className="newsletter-campaign-row-cell newsletter-campaign-row-progress-cell">
                  <Tooltip text={`Progreso de envío: ${progress.sent} de ${progress.total} emails enviados (${progress.percent}%)`}>
                    <div className="newsletter-campaign-row-progress">
                      <div className="newsletter-progress-bar newsletter-progress-bar-inline">
                        <div 
                          className="newsletter-progress-fill" 
                          style={{ width: `${progress.percent}%` }}
                        />
                        <div className="newsletter-progress-percent-label">{progress.percent}%</div>
                      </div>
                      <span className="newsletter-progress-text-inline">{progress.sent} / {progress.total}</span>
                    </div>
                  </Tooltip>
                </div>
                <div className="newsletter-campaign-row-cell newsletter-campaign-row-actions-cell">
                  <div className="action-buttons">
                    {!campaign.finished && (
                      <Tooltip text={campaign.active ? "Pausar campaña" : "Activar (arma cola y envía por lotes)"}>
                        <button
                          type="button"
                          className={`status-toggle status-play small ${campaign.active ? 'active' : ''}`}
                          title={campaign.active ? "Pausar campaña" : "Activar campaña"}
                          disabled={activatingCampaignId === campaign.id}
                          onClick={(e) => { e.stopPropagation(); toggleCampaign(campaign); }}
                        >
                          {campaign.active ? (
                            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 320 512" height="1em" width="1em"><path d="M48 64C21.5 64 0 85.5 0 112V400c0 26.5 21.5 48 48 48s48-21.5 48-48V112c0-26.5-21.5-48-48-48zm224 0c-26.5 0-48 21.5-48 48V400c0 26.5 21.5 48 48 48s48-21.5 48-48V112c0-26.5-21.5-48-48-48z" /></svg>
                          ) : (
                            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 384 512" height="1em" width="1em"><path d="M73 39c-14.8-9.1-33.4-9.4-48.5-.9S0 62.6 0 80V432c0 17.4 9.4 33.4 24.4 41.6s33.5 8.2 48.5-.9L361 297c14.3-8.7 23-24.2 23-41s-8.7-32.2-23-41L73 39z" /></svg>
                          )}
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip text="Editar campaña">
                      <button
                        type="button"
                        className="action-btn edit"
                        title="Editar campaña"
                        onClick={(e) => { e.stopPropagation(); openCampaignModal(campaign); }}
                      >
                        <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 576 512" height="1em" width="1em"><path d="M402.6 83.2l90.2 90.2c3.8 3.8 3.8 10 0 13.8L274.4 405.6l-92.8 10.3c-12.4 1.4-22.9-9.1-21.5-21.5l10.3-92.8L388.8 83.2c3.8-3.8 10-3.8 13.8 0zm162-22.9l-48.8-48.8c-15.2-15.2-39.9-15.2-55.2 0l-35.4 35.4c-3.8 3.8-3.8 10 0 13.8l90.2 90.2c3.8 3.8 10 3.8 13.8 0l35.4-35.4c15.2-15.3 15.2-40 0-55.2zM384 346.2V448H64V128h229.8c3.2 0 6.2-1.3 8.5-3.5l40-40c7.6-7.6 2.2-20.5-8.5-20.5H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V306.2c0-10.7-12.9-16-20.5-8.5l-40 40c-2.2 2.3-3.5 5.3-3.5 8.5z" /></svg>
                      </button>
                    </Tooltip>
                    <Tooltip text="Duplicar campaña (crea un borrador)">
                      <button
                        type="button"
                        className="action-btn"
                        title="Duplicar campaña"
                        onClick={(e) => { e.stopPropagation(); duplicateCampaign(campaign); }}
                      >
                        <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" height="1em" width="1em"><path d="M320 448v40c0 13.255-10.745 24-24 24H24c-13.255 0-24-10.745-24-24V120c0-13.255 10.745-24 24-24h72v296c0 30.879 25.121 56 56 56h168zm0-344V0H152c-13.255 0-24 10.745-24 24v368c0 13.255 10.745 24 24 24h272c13.255 0 24-10.745 24-24V128H344c-13.2 0-24-10.8-24-24zm120.971-31.029L375.029 7.029A24 24 0 0 0 358.059 0H352v96h96v-6.059a24 24 0 0 0-7.029-16.97z" /></svg>
                      </button>
                    </Tooltip>
                    <Tooltip text="Eliminar campaña">
                      <button
                        type="button"
                        className="action-btn delete"
                        title="Eliminar campaña"
                        onClick={(e) => { e.stopPropagation(); deleteCampaign(campaign.id); }}
                      >
                        <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" height="1em" width="1em"><path d="M432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16zM53.2 467a48 48 0 0 0 47.9 45h245.8a48 48 0 0 0 47.9-45L416 128H32z" /></svg>
                      </button>
                    </Tooltip>
                  </div>
                </div>
                <div className="newsletter-campaign-row-cell newsletter-campaign-row-checkbox-cell">
                  <Tooltip text="Marcar para eliminar varias campañas a la vez">
                    <label className="newsletter-campaign-row-checkbox newsletter-select-checkbox" onClick={e => e.stopPropagation()} aria-label="Seleccionar campaña">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleCampaignSelection(campaign.id)}
                        className="newsletter-select-checkbox-input"
                      />
                    </label>
                  </Tooltip>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal de detalle de campaña */}
      {selectedCampaign && (
        <div 
          className="newsletter-modal-overlay newsletter-detail-modal-overlay"
          onClick={() => { setSelectedCampaign(null); setShowDetailFullScreenPreview(false); }}
        >
          <div 
            className="newsletter-modal newsletter-modal-detail"
            onClick={e => e.stopPropagation()}
          >
            <div className="newsletter-modal-header newsletter-detail-modal-header">
              <h3>Detalle: {selectedCampaign.name}</h3>
              <button
                type="button"
                className="newsletter-modal-close"
                onClick={() => { setSelectedCampaign(null); setShowDetailFullScreenPreview(false); }}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div className="newsletter-modal-body newsletter-detail-modal-body">
              {/* Fila única: acciones + estado + destinatarios + resumen progreso */}
              <div className="newsletter-detail-top-row">
                <div className="newsletter-campaign-detail-actions newsletter-detail-modal-actions">
                  <Tooltip text="Editar campaña">
                    <button 
                      className="newsletter-btn newsletter-btn-edit"
                      onClick={() => { openCampaignModal(selectedCampaign); setSelectedCampaign(null); }}
                    >
                      ✏️ Editar
                    </button>
                  </Tooltip>
                  <Tooltip text="Duplicar campaña (crea un borrador)">
                    <button
                      className="newsletter-btn newsletter-btn-secondary"
                      onClick={() => { duplicateCampaign(selectedCampaign); setSelectedCampaign(null); }}
                    >
                      ⧉ Duplicar
                    </button>
                  </Tooltip>
                  {!selectedCampaign.finished && (
                    <Tooltip text={selectedCampaign.active ? "Pausar campaña" : "Activar campaña (arma cola en servidor)"}>
                      <button 
                        className={`newsletter-btn ${selectedCampaign.active ? 'newsletter-btn-danger' : 'newsletter-btn-primary'}`}
                        disabled={activatingCampaignId === selectedCampaign.id}
                        onClick={() => toggleCampaign(selectedCampaign)}
                      >
                        {activatingCampaignId === selectedCampaign.id
                          ? '⏳ Preparando…'
                          : selectedCampaign.active
                            ? '⏸️ Pausar'
                            : '▶️ Activar'}
                      </button>
                    </Tooltip>
                  )}
                </div>
                <div className="newsletter-detail-section newsletter-detail-status">
                  <span className="newsletter-detail-label">Estado</span>
                  <div className="newsletter-campaign-meta">
                    {selectedCampaign.finished ? (
                      <Badge variant="info">Finalizada</Badge>
                    ) : (
                      <Badge variant={selectedCampaign.active ? 'success' : 'secondary'}>
                        {selectedCampaign.active ? 'Activa' : 'Pausada'}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="newsletter-detail-section newsletter-detail-recipients">
                  <span className="newsletter-detail-label">Destinatarios</span>
                  <div className="newsletter-recipient-tags newsletter-recipient-tags-detail">
                    {getRecipientTags(selectedCampaign).length === 0 ? (
                      <span className="newsletter-tag newsletter-tag-empty">Sin destinatarios</span>
                    ) : (
                      getRecipientTags(selectedCampaign).map((tag, idx) => (
                        <span key={idx} className="newsletter-tag">
                          {tag.label} <span className="newsletter-tag-count">({tag.count})</span>
                        </span>
                      ))
                    )}
                  </div>
                </div>
                <div className="newsletter-detail-section newsletter-detail-progress-summary">
                  <span className="newsletter-detail-label">Progreso</span>
                  <span className="newsletter-detail-progress-inline">
                    {(() => {
                      const st = getCampaignStats(selectedCampaign);
                      return `${getProgress(selectedCampaign).percent}% · ${st.sent} enviados · ${st.errors} errores · ${st.pending} pendientes`;
                    })()}
                  </span>
                </div>
              </div>

              {/* Solo la barra de progreso a ancho completo */}
              <div className="newsletter-detail-progress-bar-row">
                <div className="newsletter-progress-bar">
                  <div 
                    className="newsletter-progress-fill" 
                    style={{ width: `${getProgress(selectedCampaign).percent}%` }}
                  />
                  <div className="newsletter-progress-percent-label">{getProgress(selectedCampaign).percent}%</div>
                </div>
              </div>

              <div className="newsletter-deliveries-panel">
                <div className="newsletter-deliveries-header">
                  <h4>Registro de envíos</h4>
                  <p className="newsletter-deliveries-hint">
                    Cada dirección se envía una sola vez: ID único por email, reserva atómica antes del correo y sin re-encolar envíos ya exitosos.
                  </p>
                  {(() => {
                    const st = getCampaignStats(selectedCampaign);
                    return (
                      <div className="newsletter-deliveries-stats">
                        <span className="newsletter-delivery-stat newsletter-delivery-stat--ok">
                          Enviados: <strong>{st.sent}</strong>
                        </span>
                        <span className="newsletter-delivery-stat newsletter-delivery-stat--err">
                          Errores: <strong>{st.errors}</strong>
                        </span>
                        <span className="newsletter-delivery-stat newsletter-delivery-stat--pending">
                          Pendientes: <strong>{st.pending}</strong>
                        </span>
                        <span className="newsletter-delivery-stat">
                          Total: <strong>{st.total}</strong>
                        </span>
                      </div>
                    );
                  })()}
                </div>
                <div className="newsletter-deliveries-toolbar">
                  <div className="newsletter-deliveries-tabs">
                    {[
                      { id: 'sent', label: 'Enviados' },
                      { id: 'error', label: 'Con error' },
                      { id: 'pending', label: 'Pendientes' }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        className={`newsletter-deliveries-tab ${deliveryTab === tab.id ? 'active' : ''}`}
                        onClick={() => setDeliveryTab(tab.id)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                  <input
                    type="search"
                    className="newsletter-search-input newsletter-deliveries-search"
                    placeholder="Buscar email…"
                    value={deliverySearch}
                    onChange={(e) => setDeliverySearch(e.target.value)}
                  />
                  <button
                    type="button"
                    className="newsletter-btn newsletter-btn-small newsletter-btn-secondary"
                    onClick={() => loadCampaignDeliveries(selectedCampaign.id, deliveryTab)}
                    disabled={loadingDeliveries}
                    title="Actualizar lista"
                  >
                    {loadingDeliveries ? '…' : '↻'}
                  </button>
                  <button
                    type="button"
                    className="newsletter-btn newsletter-btn-small newsletter-btn-secondary"
                    onClick={exportDeliveriesCsv}
                    disabled={filteredDeliveryRows.length === 0}
                    title="Descargar CSV"
                  >
                    CSV
                  </button>
                </div>
                <div className="newsletter-deliveries-table-wrap">
                  {loadingDeliveries ? (
                    <p className="newsletter-deliveries-muted">Cargando…</p>
                  ) : filteredDeliveryRows.length === 0 ? (
                    <p className="newsletter-deliveries-muted">
                      {deliverySearch
                        ? 'Ningún email coincide con la búsqueda.'
                        : deliveryTab === 'sent'
                          ? 'Todavía no hay envíos registrados.'
                          : deliveryTab === 'error'
                            ? 'No hay envíos con error.'
                            : 'No quedan emails en la cola.'}
                    </p>
                  ) : (
                    <table className="newsletter-deliveries-table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Estado</th>
                          <th>Fecha</th>
                          {deliveryTab === 'error' && <th>Error</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDeliveryRows.map((row) => (
                          <tr key={row.id}>
                            <td className="newsletter-deliveries-email">{row.email}</td>
                            <td>
                              {row.status === 'sent' && <Badge variant="success">Enviado</Badge>}
                              {row.status === 'error' && <Badge variant="warning">Error</Badge>}
                              {row.status === 'pending' && <Badge variant="secondary">Pendiente</Badge>}
                            </td>
                            <td>{formatDeliveryDate(row.sentAt)}</td>
                            {deliveryTab === 'error' && (
                              <td className="newsletter-deliveries-error-cell">{row.error || '—'}</td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                {deliveryRows.length >= DELIVERY_LIST_LIMIT && (
                  <p className="newsletter-deliveries-muted">
                    Mostrando los primeros {DELIVERY_LIST_LIMIT} registros.
                  </p>
                )}
              </div>

              <div className="newsletter-html-preview newsletter-detail-html-preview">
                <div className="newsletter-detail-preview-header">
                  <h4>Vista Previa del Email</h4>
                  <Tooltip text="Ver vista previa a pantalla completa">
                    <button
                      type="button"
                      className="newsletter-btn newsletter-btn-small newsletter-btn-secondary"
                      onClick={(e) => { e.stopPropagation(); setShowDetailFullScreenPreview(true); }}
                      title="Pantalla completa"
                    >
                      🖵 Pantalla completa
                    </button>
                  </Tooltip>
                </div>
                <NewsletterEmailPreview
                  html={withPreviewUnsubscribeFooter(selectedCampaign.html)}
                  onFullScreen={() => setShowDetailFullScreenPreview(true)}
                  height={460}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {showDetailFullScreenPreview && selectedCampaign && (
        <div className="newsletter-fullscreen-overlay" onClick={() => setShowDetailFullScreenPreview(false)}>
          <div className="newsletter-fullscreen-content newsletter-fullscreen-preview" onClick={(e) => e.stopPropagation()}>
            <div className="newsletter-fullscreen-header">
              <span>Vista Previa del Email</span>
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                <button
                  className="newsletter-btn newsletter-btn-secondary newsletter-btn-small"
                  onClick={() => setShowDetailFullScreenPreview(false)}
                >
                  Cerrar
                </button>
              </div>
            </div>
            <NewsletterEmailPreview
              html={withPreviewUnsubscribeFooter(selectedCampaign.html)}
              height={window.innerHeight - 140}
            />
          </div>
        </div>
      )}

    </div>
  );

  const renderGroups = () => {
    const isAutoGroup = (g) => g && (AUTO_GROUP_IDS.has(g.id) || g.auto === true);
    const formatSyncedAt = (ts) => {
      try {
        const ms = ts?.toMillis?.() ?? (ts?.seconds ? ts.seconds * 1000 : null);
        if (!ms) return null;
        return new Date(ms).toLocaleString();
      } catch {
        return null;
      }
    };
    return (
    <div className="newsletter-tab-content">
      <div className="newsletter-tab-header">
        <h2>Grupos de Emails</h2>
        <div className="newsletter-tab-header-actions">
          <Tooltip text="Crear nuevo grupo">
            <button
              className="newsletter-btn newsletter-btn-primary"
              onClick={() => openGroupModal()}
            >
              <span>+</span> Nuevo Grupo
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="newsletter-import-historical">
        <p className="newsletter-import-historical-hint">
          Los grupos <strong>Clientes</strong>, <strong>Suscriptores</strong> y <strong>Contactos</strong> se
          actualizan solos al vender, suscribirse o enviar el formulario de contacto. Usá importar historial
          solo una vez si faltan registros antiguos.
        </p>
        <div className="newsletter-import-historical-actions">
          <button
            type="button"
            className="newsletter-btn newsletter-btn-small newsletter-btn-secondary"
            onClick={importCustomersHistory}
            disabled={importingHistory !== null}
          >
            {importingHistory === 'customers' ? 'Importando…' : 'Importar clientes (historial)'}
          </button>
          <button
            type="button"
            className="newsletter-btn newsletter-btn-small newsletter-btn-secondary"
            onClick={importSubscribersHistory}
            disabled={importingHistory !== null}
          >
            {importingHistory === 'subscribers' ? 'Importando…' : 'Importar suscriptores (historial)'}
          </button>
          <button
            type="button"
            className="newsletter-btn newsletter-btn-small newsletter-btn-secondary"
            onClick={importContactsHistory}
            disabled={importingHistory !== null}
          >
            {importingHistory === 'contacts' ? 'Importando…' : 'Importar contactos (historial)'}
          </button>
        </div>
      </div>

      <div className="newsletter-email-cleaner">
        <div className="newsletter-email-cleaner-search">
          <span className="newsletter-email-cleaner-icon" aria-hidden="true">🔎</span>
          <input
            type="search"
            className="newsletter-input"
            placeholder="Buscar (ej: juan, juan@, parte del email)…"
            value={emailSearchQuery}
            onChange={(e) => setEmailSearchQuery(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          {emailSearching && <span className="newsletter-email-cleaner-spin">Buscando…</span>}
        </div>
        <p className="newsletter-email-cleaner-hint">
          Buscá por el texto de la izquierda del @ (o el email completo). Aparece solo al escribir. Quitalo de un grupo o de todos con un clic (sin confirmaciones).
        </p>

        {emailSearchResult && (
          <div className="newsletter-email-cleaner-result">
            {emailSearchResult.matches.length === 0 ? (
              <p className="newsletter-deliveries-muted">
                Sin coincidencias para <strong>{emailSearchResult.query}</strong>.
              </p>
            ) : (
              <>
                <div className="newsletter-email-cleaner-result-head">
                  <span>
                    {emailSearchResult.matches.length} coincidencia(s){emailSearchResult.truncated ? ' (mostrando las primeras)' : ''}:
                  </span>
                </div>
                <div className="newsletter-email-cleaner-matches">
                  {emailSearchResult.matches.map((match) => (
                    <div key={match.email} className="newsletter-email-cleaner-match">
                      <div className="newsletter-email-cleaner-match-head">
                        <span className="newsletter-email-cleaner-match-email">{match.email}</span>
                        <button
                          type="button"
                          className="newsletter-btn newsletter-btn-small newsletter-btn-danger"
                          onClick={() => purgeEmailEverywhere(match.email)}
                          disabled={emailActionBusy !== null}
                        >
                          {emailActionBusy === `all:${match.email}` ? 'Eliminando…' : '🧹 De todos'}
                        </button>
                      </div>
                      <div className="newsletter-email-cleaner-groups">
                        {match.groups.map((g) => (
                          <div key={g.id} className="newsletter-email-cleaner-group">
                            <span className="newsletter-email-cleaner-group-name">
                              {g.name}{g.auto && <Badge variant="info">AUTO</Badge>}
                            </span>
                            <button
                              type="button"
                              className="newsletter-btn newsletter-btn-small newsletter-btn-secondary"
                              onClick={() => removeFoundEmailFromGroup(g.id, match.email)}
                              disabled={emailActionBusy !== null}
                            >
                              {emailActionBusy === `${g.id}:${match.email}` ? 'Quitando…' : 'Quitar'}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {emailSearchResult.matches.some((m) => m.groups.some((g) => g.auto)) && (
                  <p className="newsletter-email-cleaner-note">
                    Los grupos <strong>AUTO</strong> se sincronizan con datos reales (ventas, suscripciones, contactos):
                    si el contacto vuelve a generar actividad puede reaparecer. "De todos" también lo borra de la
                    lista de suscripciones.
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="newsletter-groups-layout">
        <div className="newsletter-groups-list">
          <h3>Todos los Grupos</h3>
          {groups.map(group => {
            const auto = isAutoGroup(group);
            const syncedLabel = auto ? formatSyncedAt(group.syncedAt) : null;
            return (
              <div
                key={group.id}
                className={`newsletter-group-card ${selectedGroup?.id === group.id ? 'selected' : ''}`}
                onClick={() => setSelectedGroup(group)}
              >
                <div className="newsletter-group-header">
                  <h4>
                    {groupTitle(group)}
                    {auto && (
                      <Badge variant="info">
                        AUTO
                      </Badge>
                    )}
                  </h4>
                  <div className="newsletter-group-actions">
                    {!auto && (
                      <>
                        <Tooltip text="Editar grupo">
                          <button
                            className="newsletter-btn newsletter-btn-small newsletter-btn-secondary"
                            onClick={(e) => { e.stopPropagation(); openGroupModal(group); }}
                          >
                            ✏️
                          </button>
                        </Tooltip>
                        <Tooltip text="Eliminar grupo">
                          <button
                            className="newsletter-btn newsletter-btn-small newsletter-btn-danger"
                            onClick={(e) => { e.stopPropagation(); deleteGroup(group.id); }}
                          >
                            🗑️
                          </button>
                        </Tooltip>
                      </>
                    )}
                  </div>
                </div>
                <p className="newsletter-group-count">
                  {group.emailCount ?? 0} emails
                </p>
                {group.id === CUSTOMERS_GROUP_ID && (
                  <div className="newsletter-group-source-breakdown">
                    {CUSTOMER_SOURCE_SEGMENTS.map((segment) => (
                      <span key={segment.id}>
                        {segment.label}: {getCustomerSourceCount(segment.countKey)}
                      </span>
                    ))}
                  </div>
                )}
                {auto && syncedLabel && (
                  <p className="newsletter-group-count" style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem' }}>
                    Última sincronización: {syncedLabel}
                  </p>
                )}
                {auto && !(group.emailCount > 0) && (
                  <p className="newsletter-group-count" style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem' }}>
                    {group.id === CONTACTOS_GROUP_ID
                      ? 'Se irá llenando cuando alguien envíe el formulario de contacto.'
                      : group.id === SUBSCRIBERS_GROUP_ID
                        ? 'Se irá llenando al suscribirse en la web.'
                        : 'Se irá llenando al registrar ventas o pedidos.'}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {selectedGroup && (
          <div className="newsletter-group-detail">
            <div className="newsletter-group-detail-header">
              <h3>{groupTitle(selectedGroup)}</h3>
              <Badge variant="secondary">
                {selectedGroup.emailCount ?? groupMembers.length ?? 0} emails
              </Badge>
            </div>
            
            <div className="newsletter-group-emails">
              <div className="newsletter-emails-header">
                <h4>Lista de Emails</h4>
                <div className="newsletter-search-container">
                  <input
                    type="text"
                    className="newsletter-search-input"
                    placeholder="🔍 Buscar emails..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="newsletter-search-clear"
                      onClick={() => setSearchTerm('')}
                      title="Limpiar búsqueda"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              
              <div className="newsletter-emails-stats">
                <span>
                  Mostrando {filteredEmails.length} de {selectedGroup.emailCount ?? groupMembers.length ?? 0} emails
                  {loadingGroupMembers && ' (cargando…)'}
                  {searchTerm && ` (filtrado por "${searchTerm}")`}
                </span>
              </div>
              
              <div className="newsletter-emails-list">
                {filteredEmails.length > 0 ? (
                  filteredEmails.map((email, index) => (
                    <div key={index} className="newsletter-email-item">
                      <span className="newsletter-email-text">{email}</span>
                      <div className="newsletter-email-actions">
                        <Badge variant="success">Válido</Badge>
                        <Tooltip text="Eliminar email del grupo">
                          <button
                            className="newsletter-btn newsletter-btn-small newsletter-btn-danger"
                            onClick={() => removeEmailFromGroup(selectedGroup.id, email)}
                          >
                            🗑️
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="newsletter-no-results">
                    {searchTerm ? (
                      <p>No se encontraron emails que coincidan con "{searchTerm}"</p>
                    ) : (
                      <p>No hay emails en este grupo</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    );
  };

  const renderAnalytics = () => {
    const agg = campaigns.reduce(
      (acc, c) => {
        const st = getCampaignStats(c);
        acc.recipients += st.total;
        acc.sent += st.sent;
        acc.errors += st.errors;
        acc.pending += st.pending;
        return acc;
      },
      { recipients: 0, sent: 0, errors: 0, pending: 0 }
    );
    const attempted = agg.sent + agg.errors;
    const deliveryRate = attempted > 0 ? Math.round((agg.sent / attempted) * 100) : 0;

    return (
      <div className="newsletter-tab-content">
        <div className="newsletter-tab-header">
          <h2>Métricas de entrega</h2>
        </div>

        <div className="newsletter-metrics-cards">
          <div className="newsletter-metric-card">
            <span className="newsletter-metric-value">{agg.recipients.toLocaleString('es-AR')}</span>
            <span className="newsletter-metric-label">Destinatarios</span>
          </div>
          <div className="newsletter-metric-card newsletter-metric-card--ok">
            <span className="newsletter-metric-value">{agg.sent.toLocaleString('es-AR')}</span>
            <span className="newsletter-metric-label">Enviados</span>
          </div>
          <div className="newsletter-metric-card newsletter-metric-card--err">
            <span className="newsletter-metric-value">{agg.errors.toLocaleString('es-AR')}</span>
            <span className="newsletter-metric-label">Errores</span>
          </div>
          <div className="newsletter-metric-card">
            <span className="newsletter-metric-value">{agg.pending.toLocaleString('es-AR')}</span>
            <span className="newsletter-metric-label">Pendientes</span>
          </div>
          <div className="newsletter-metric-card newsletter-metric-card--rate">
            <span className="newsletter-metric-value">{deliveryRate}%</span>
            <span className="newsletter-metric-label">Tasa de entrega</span>
          </div>
        </div>

        <div className="newsletter-analytics-grid">
          <div className="newsletter-analytics-card">
            <h3>Estadísticas generales</h3>
            <div className="newsletter-analytics-stats">
              <div className="newsletter-stat">
                <span className="newsletter-stat-value">{stats.totalCampaigns}</span>
                <span className="newsletter-stat-label">Campañas totales</span>
              </div>
              <div className="newsletter-stat">
                <span className="newsletter-stat-value">{stats.activeCampaigns}</span>
                <span className="newsletter-stat-label">Campañas activas</span>
              </div>
              <div className="newsletter-stat">
                <span className="newsletter-stat-value">{stats.totalGroups}</span>
                <span className="newsletter-stat-label">Grupos</span>
              </div>
              <div className="newsletter-stat">
                <span className="newsletter-stat-value">{stats.totalEmails}</span>
                <span className="newsletter-stat-label">Emails totales</span>
              </div>
            </div>
          </div>

          <div className="newsletter-analytics-card">
            <h3>Detalle por campaña</h3>
            {campaigns.length === 0 ? (
              <p className="newsletter-deliveries-muted">Todavía no hay campañas.</p>
            ) : (
              <div className="newsletter-metrics-table-wrap">
                <table className="newsletter-deliveries-table newsletter-metrics-table">
                  <thead>
                    <tr>
                      <th>Campaña</th>
                      <th>Estado</th>
                      <th>Enviados</th>
                      <th>Errores</th>
                      <th>Total</th>
                      <th>Progreso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((campaign) => {
                      const st = getCampaignStats(campaign);
                      const progress = getProgress(campaign);
                      const statusBadge = campaign.finished
                        ? <Badge variant="info">Finalizada</Badge>
                        : campaign.status === 'scheduled' && campaign.scheduledAt && !campaign.active
                          ? <Badge variant="info">Programada</Badge>
                          : campaign.status === 'draft' && !campaign.active
                            ? <Badge variant="warning">Borrador</Badge>
                            : <Badge variant={campaign.active ? 'success' : 'secondary'}>{campaign.active ? 'Activa' : 'Pausada'}</Badge>;
                      return (
                        <tr key={campaign.id}>
                          <td>{campaign.name}</td>
                          <td>{statusBadge}</td>
                          <td>{st.sent.toLocaleString('es-AR')}</td>
                          <td>{st.errors.toLocaleString('es-AR')}</td>
                          <td>{st.total.toLocaleString('es-AR')}</td>
                          <td>
                            <div className="newsletter-progress-bar newsletter-progress-bar-inline">
                              <div className="newsletter-progress-fill" style={{ width: `${progress.percent}%` }} />
                              <div className="newsletter-progress-percent-label">{progress.percent}%</div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderUnsubscribed = () => {
    const term = unsubscribedSearch.trim().toLowerCase();
    const filtered = term
      ? unsubscribed.filter((u) => (u.email || '').toLowerCase().includes(term))
      : unsubscribed;
    const fmtDate = (ms) => {
      if (!ms) return '—';
      try {
        return new Date(ms).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
      } catch {
        return '—';
      }
    };
    const sourceLabel = (s) => (s === 'email_link' ? 'Enlace del email' : (s || '—'));

    return (
      <div className="newsletter-tab-content">
        <div className="newsletter-tab-header">
          <h2>Desuscriptos</h2>
          <button
            type="button"
            className="newsletter-btn newsletter-btn-secondary newsletter-btn-small"
            onClick={loadUnsubscribed}
            disabled={loadingUnsubscribed}
          >
            {loadingUnsubscribed ? 'Actualizando…' : '↻ Actualizar'}
          </button>
        </div>

        <p className="newsletter-deliveries-muted" style={{ marginBottom: 'var(--spacing-md)' }}>
          Contactos que pidieron la baja. Ya fueron quitados de los grupos y no reciben campañas. Acá podés ver el registro y eliminarlo si querés limpiarlo.
        </p>

        <div className="newsletter-unsub-toolbar">
          <input
            type="search"
            className="newsletter-input newsletter-unsub-search"
            placeholder="Buscar por email…"
            value={unsubscribedSearch}
            onChange={(e) => setUnsubscribedSearch(e.target.value)}
          />
          <span className="newsletter-unsub-count">
            {filtered.length.toLocaleString('es-AR')} de {unsubscribed.length.toLocaleString('es-AR')}
          </span>
        </div>

        {loadingUnsubscribed ? (
          <p className="newsletter-deliveries-muted">Cargando…</p>
        ) : filtered.length === 0 ? (
          <p className="newsletter-deliveries-muted">
            {unsubscribed.length === 0 ? 'No hay contactos desuscriptos.' : 'Sin resultados para la búsqueda.'}
          </p>
        ) : (
          <div className="newsletter-metrics-table-wrap">
            <table className="newsletter-deliveries-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Fecha de baja</th>
                  <th>Origen</th>
                  <th aria-label="Acciones"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const key = u.id || u.email;
                  return (
                    <tr key={key}>
                      <td className="newsletter-deliveries-email">{u.email}</td>
                      <td>{fmtDate(u.unsubscribedAt)}</td>
                      <td>{sourceLabel(u.source)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="newsletter-btn newsletter-btn-danger newsletter-btn-small"
                          onClick={() => deleteUnsubscribed(u)}
                          disabled={deletingUnsubscribed === key}
                        >
                          {deletingUnsubscribed === key ? 'Eliminando…' : '🗑️ Eliminar'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="newsletter-loading">
        <div className="newsletter-spinner"></div>
        <p>Cargando datos...</p>
      </div>
    );
  }

  return (
    <div className="newsletter-container">
      {/* Header */}
      <header className="newsletter-header">
        <div className="newsletter-header-content">
          <div className="newsletter-logo">
            <div className="newsletter-logo-icon">📧</div>
            <h1>Envíos de email masivo</h1>
          </div>
          
          <div className="newsletter-header-actions">
            <div className="newsletter-status-badge">
              <div className={`newsletter-status-indicator ${stats.activeCampaigns > 0 ? 'active' : 'paused'}`}></div>
              <span>{stats.activeCampaigns} campañas activas</span>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar: clases newsletter-layout-* para evitar conflictos; siempre visible a la izquierda */}
      <aside className="newsletter-sidebar newsletter-layout-aside">
        <nav className="newsletter-nav newsletter-layout-nav">
          <button 
            type="button"
            className={`newsletter-nav-item newsletter-layout-nav-btn ${activeTab === 'campaigns' ? 'active' : ''}`}
            onClick={() => setActiveTab('campaigns')}
          >
            <span>📧</span> Campañas
          </button>
          <button 
            type="button"
            className={`newsletter-nav-item newsletter-layout-nav-btn ${activeTab === 'groups' ? 'active' : ''}`}
            onClick={() => setActiveTab('groups')}
          >
            <span>👥</span> Grupos
          </button>
          <button 
            type="button"
            className={`newsletter-nav-item newsletter-layout-nav-btn ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <span>📊</span> Analíticas
          </button>
          <button 
            type="button"
            className={`newsletter-nav-item newsletter-layout-nav-btn ${activeTab === 'unsubscribed' ? 'active' : ''}`}
            onClick={() => setActiveTab('unsubscribed')}
          >
            <span>🚫</span> Desuscriptos
          </button>
          <button 
            type="button"
            className="newsletter-nav-item newsletter-nav-item-help newsletter-layout-nav-btn newsletter-layout-nav-btn-help"
            onClick={() => setShowHelpModal(true)}
          >
            <span>❓</span> Cómo funciona
          </button>
        </nav>

        <div className="newsletter-quick-stats newsletter-layout-stats">
          <Tooltip
            text={stats.activeCampaigns > 0
              ? "Sistema activo: hay al menos una campaña en estado Activa que está enviando emails en este momento."
              : "Sistema inactivo: no hay ninguna campaña activa. Las campañas están pausadas o finalizadas. Activá una campaña para que empiece a enviar."
            }
            position="right"
          >
            <div className="newsletter-stat-card newsletter-layout-stat-card">
              <h4>Resumen</h4>
              <div className="newsletter-progress-bar summary">
                <div 
                  className="newsletter-progress-fill" 
                  style={{ width: `${stats.activeCampaigns > 0 ? 75 : 25}%` }}
                />
              </div>
              <p className="newsletter-progress-text">
                Sistema {stats.activeCampaigns > 0 ? 'activo' : 'inactivo'}
              </p>
            </div>
          </Tooltip>
        </div>
      </aside>

      {/* Main Content */}
      <main className="newsletter-main">
        {activeTab === 'campaigns' && renderCampaigns()}
        {activeTab === 'groups' && renderGroups()}
        {activeTab === 'analytics' && renderAnalytics()}
        {activeTab === 'unsubscribed' && renderUnsubscribed()}
      </main>

      {/* Modal: no se cierra al hacer clic en el fondo en campañas; solo con Cancelar o × (y confirm si hay contenido) */}
      {showModal && modalType === 'group' && (
        <div
          className="newsletter-modal-overlay"
          onClick={(e) => {
            if (e.target !== e.currentTarget) return;
            setShowModal(false);
          }}
        >
          <div className="newsletter-modal newsletter-modal-large" onClick={e => e.stopPropagation()}>
            <div className="newsletter-modal-header">
              <h3>{editingItem ? 'Editar Grupo' : 'Nuevo Grupo'}</h3>
              <button
                type="button"
                className="newsletter-modal-close"
                onClick={closeCampaignModal}
              >
                ×
              </button>
            </div>
            
            <div className="newsletter-modal-body">
                <div>
                  <div className="newsletter-form-group">
                    <label className="newsletter-label">
                      Nombre del Grupo <span className="newsletter-required">*</span>
                    </label>
                    <input 
                      className="newsletter-input"
                      type="text"
                      placeholder="Ej: Clientes VIP"
                      value={groupForm.name}
                      onChange={e => setGroupForm({...groupForm, name: e.target.value})}
                    />
                  </div>
                  
                  <div className="newsletter-form-group">
                    <label className="newsletter-label">
                      Emails <span className="newsletter-required">*</span>
                    </label>
                    <textarea 
                      className="newsletter-textarea"
                      placeholder="Ingresa los emails separados por líneas, comas o punto y coma"
                      value={groupForm.emails}
                      onChange={e => setGroupForm({...groupForm, emails: e.target.value})}
                      rows={8}
                    />
                    <small style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                      Emails válidos serán filtrados automáticamente
                    </small>
                  </div>
                </div>
            </div>
            <div className="newsletter-modal-footer">
              <button
                type="button"
                className="newsletter-btn newsletter-btn-secondary"
                onClick={closeCampaignModal}
              >
                Cancelar
              </button>
              <button
                className="newsletter-btn newsletter-btn-primary"
                onClick={saveGroup}
              >
                {editingItem ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Página de edición de campaña: herramientas (izq) + vista previa en vivo (der) */}
      {showModal && modalType === 'campaign' && (
        <div className="newsletter-editor-overlay">
          <div className="newsletter-editor-page">
            <header className="newsletter-editor-topbar">
              <div className="newsletter-editor-topbar-left">
                <button type="button" className="newsletter-editor-back" onClick={closeCampaignModal} title="Cerrar editor">←</button>
                <div className="newsletter-editor-topbar-title">
                  <span className="newsletter-editor-topbar-eyebrow">{editingItem ? 'Editar campaña' : 'Nueva campaña'}</span>
                  <strong className="newsletter-editor-topbar-name">{(campaignForm.name || '').trim() || 'Sin título'}</strong>
                </div>
              </div>
              <div className="newsletter-editor-topbar-actions">
                <button type="button" className="newsletter-btn newsletter-btn-secondary" onClick={closeCampaignModal}>Cancelar</button>
                <button type="button" className="newsletter-btn newsletter-btn-secondary" onClick={sendTestEmail} disabled={sendingTest}>{sendingTest ? 'Enviando…' : '✉️ Enviar prueba'}</button>
                <button type="button" className="newsletter-btn newsletter-btn-secondary" onClick={() => saveCampaign({ asDraft: true })}>💾 Guardar borrador</button>
                <button type="button" className="newsletter-btn newsletter-btn-primary" onClick={() => saveCampaign()}>{editingItem ? 'Guardar' : 'Crear'}</button>
              </div>
            </header>
            <div className="newsletter-editor-split">
              <div className="newsletter-editor-panel">
                  <div className="newsletter-form-group">
                    <label className="newsletter-label">
                      Nombre de la Campaña <span className="newsletter-required">*</span>
                    </label>
                    <input 
                      className="newsletter-input"
                      type="text"
                      placeholder="Ej: Promoción de Verano 2024"
                      value={campaignForm.name}
                      onChange={e => setCampaignForm({...campaignForm, name: e.target.value})}
                    />
                  </div>
                  
                  <details className="newsletter-form-group newsletter-collapse" open>
                    <summary className="newsletter-collapse-head">
                      <span className="newsletter-collapse-title">
                        Destinatarios <span className="newsletter-required">*</span>
                        {(campaignForm.selectedRecipientIds || []).length > 0 && (
                          <span className="newsletter-collapse-badge">
                            {getRecipientTotal({ selectedRecipientIds: campaignForm.selectedRecipientIds }).toLocaleString('es-AR')} contactos
                          </span>
                        )}
                      </span>
                      <span className="newsletter-collapse-chevron" aria-hidden="true">▾</span>
                    </summary>
                    <div className="newsletter-collapse-body">
                    <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: 'var(--spacing-sm)' }}>
                      Elegí uno o más orígenes. La campaña envía a la unión de todos (sin duplicar emails).
                    </p>
                    <div className="newsletter-recipients-section">
                      <p className="newsletter-recipients-section-title">Clientes por canal</p>
                      <div className="newsletter-recipients-multi">
                        {CUSTOMER_SOURCE_SEGMENTS.map((segment) => (
                          <label key={segment.id} className="newsletter-recipient-option">
                            <input
                              type="checkbox"
                              checked={(campaignForm.selectedRecipientIds || []).includes(segment.id)}
                              onChange={() => toggleRecipientId(segment.id)}
                            />
                            <span className="newsletter-recipient-label">
                              {segment.label}{' '}
                              <span className="newsletter-recipient-count">
                                ({getCustomerSourceCount(segment.countKey)} emails)
                              </span>
                            </span>
                          </label>
                        ))}
                        <label className="newsletter-recipient-option">
                          <input
                            type="checkbox"
                            checked={(campaignForm.selectedRecipientIds || []).includes(CUSTOMERS_GROUP_ID)}
                            onChange={() => toggleRecipientId(CUSTOMERS_GROUP_ID)}
                          />
                          <span className="newsletter-recipient-label">
                            Todos los clientes (todos los canales){' '}
                            <span className="newsletter-recipient-count">
                              ({getGroupEmailCount(CUSTOMERS_GROUP_ID)} emails)
                            </span>
                          </span>
                        </label>
                      </div>
                    </div>
                    <div className="newsletter-recipients-section">
                      <p className="newsletter-recipients-section-title">Otros orígenes automáticos</p>
                      <div className="newsletter-recipients-multi">
                        <label className="newsletter-recipient-option">
                          <input
                            type="checkbox"
                            checked={(campaignForm.selectedRecipientIds || []).includes(SUBSCRIBERS_GROUP_ID)}
                            onChange={() => toggleRecipientId(SUBSCRIBERS_GROUP_ID)}
                          />
                          <span className="newsletter-recipient-label">
                            Suscriptores (web){' '}
                            <span className="newsletter-recipient-count">
                              ({getGroupEmailCount(SUBSCRIBERS_GROUP_ID)} emails)
                            </span>
                          </span>
                        </label>
                        <label className="newsletter-recipient-option">
                          <input
                            type="checkbox"
                            checked={(campaignForm.selectedRecipientIds || []).includes(CONTACTOS_GROUP_ID)}
                            onChange={() => toggleRecipientId(CONTACTOS_GROUP_ID)}
                          />
                          <span className="newsletter-recipient-label">
                            Contactos (formulario){' '}
                            <span className="newsletter-recipient-count">
                              ({getGroupEmailCount(CONTACTOS_GROUP_ID)} emails)
                            </span>
                          </span>
                        </label>
                      </div>
                    </div>
                    {groups.filter((g) => !HIDDEN_CAMPAIGN_GROUP_IDS.has(g.id) && !AUTO_GROUP_IDS.has(g.id)).length > 0 && (
                      <div className="newsletter-recipients-section">
                        <p className="newsletter-recipients-section-title">Grupos manuales</p>
                        <div className="newsletter-recipients-multi">
                          {groups
                            .filter((g) => !HIDDEN_CAMPAIGN_GROUP_IDS.has(g.id) && !AUTO_GROUP_IDS.has(g.id))
                            .map((group) => (
                              <label key={group.id} className="newsletter-recipient-option">
                                <input
                                  type="checkbox"
                                  checked={(campaignForm.selectedRecipientIds || []).includes(group.id)}
                                  onChange={() => toggleRecipientId(group.id)}
                                />
                                <span className="newsletter-recipient-label">
                                  {group.name}{' '}
                                  <span className="newsletter-recipient-count">
                                    ({group.emailCount ?? 0} emails)
                                  </span>
                                </span>
                              </label>
                            ))}
                        </div>
                      </div>
                    )}
                    {(campaignForm.selectedRecipientIds || []).length > 0 && (
                      <div className="newsletter-reach-summary">
                        📣 Alcance estimado:{' '}
                        <strong>
                          {getRecipientTotal({ selectedRecipientIds: campaignForm.selectedRecipientIds }).toLocaleString('es-AR')}
                        </strong>{' '}
                        contactos
                        <span className="newsletter-reach-note">El servidor deduplica los repetidos al enviar.</span>
                      </div>
                    )}
                    </div>
                  </details>

                  {/* Selector de modo de edición */}
                  <div className="newsletter-form-group">
                    <div className="newsletter-editor-mode-switch">
                      <button
                        type="button"
                        className={`newsletter-btn newsletter-btn-small ${editorMode === 'builder' ? 'newsletter-btn-primary' : 'newsletter-btn-secondary'}`}
                        onClick={() => setEditorMode('builder')}
                      >
                        🧱 Constructor visual
                      </button>
                      <button
                        type="button"
                        className={`newsletter-btn newsletter-btn-small ${editorMode === 'html' ? 'newsletter-btn-primary' : 'newsletter-btn-secondary'}`}
                        onClick={() => setEditorMode('html')}
                      >
                        {'</>'} HTML avanzado / IA
                      </button>
                    </div>
                  </div>

                  {editorMode === 'builder' && (
                    <div className="newsletter-form-group">
                      <NewsletterBuilder
                        blocks={campaignForm.blocks || []}
                        design={campaignForm.design || {}}
                        onBlocksChange={(next) => setCampaignForm((prev) => ({ ...prev, blocks: next }))}
                        onDesignChange={(next) => setCampaignForm((prev) => ({ ...prev, design: next }))}
                        onOpenProductPicker={(blockId) => { setProductPickerTarget(blockId); setShowOfferProductPicker(true); }}
                      />
                      <div className="newsletter-unsubscribe-note" style={{ marginTop: 'var(--spacing-md)' }}>
                        <span className="newsletter-unsubscribe-note-badge">Footer automático</span>
                        Cada envío agrega un botón <strong>Desuscribirme</strong> firmado por destinatario; no hace falta incluirlo en el diseño.
                      </div>
                    </div>
                  )}

                  {editorMode === 'html' && (
                  <>
                  {/* Plantilla con ofertas y asistente de IA */}
                  <div className="newsletter-form-group newsletter-offer-section">
                    <label className="newsletter-label">Plantilla con asistente de IA</label>
                    <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: 'var(--spacing-sm)' }}>
                      Describí en el prompt todo lo que querés: textos, botones, enlaces (pegalos tal cual) y cómo mostrar los productos seleccionados. La IA arma el cuerpo del email y el sistema le pone logo/cabecera + footer con botón de desuscripción visible y seguro.
                    </p>
                    <div className="newsletter-offer-products-row">
                      <button
                        type="button"
                        className="newsletter-btn newsletter-btn-secondary newsletter-btn-small"
                        onClick={() => { setProductPickerTarget('ai'); setShowOfferProductPicker(true); }}
                      >
                        + Agregar productos a la plantilla
                      </button>
                    </div>
                    {offerProducts.length > 0 && (
                      <div className="newsletter-offer-selected-grid">
                        {offerProducts.map((p) => (
                          <div key={p.id} className="newsletter-offer-selected-card">
                            {p.displayImageUrl && (
                              <img src={p.displayImageUrl} alt={p.model || p.name || ''} className="newsletter-offer-selected-img" />
                            )}
                            <span className="newsletter-offer-selected-name">{p.model || p.name || 'Producto'}</span>
                            <button
                              type="button"
                              className="newsletter-offer-remove-btn"
                              onClick={() => removeOfferProduct(p.id)}
                              title="Quitar de la plantilla"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="newsletter-ai-row">
                      <textarea
                        className="newsletter-textarea"
                        placeholder={`Describí el email que querés. Ejemplos:\n- "Newsletter de ofertas de verano. Título 'Verano Priball'. Texto corto y un botón 'Ver catálogo' con enlace https://www.priball.com/productos. Mostrá los productos seleccionados en una grilla con precio y enlace al detalle."\n- "Email de novedades para clientes mayoristas con el botón 'Solicitar presupuesto' apuntando a https://www.priball.com/contacto".`}
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        rows={5}
                        style={{ marginTop: 'var(--spacing-sm)' }}
                      />
                      <small style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                        La IA solo usará los enlaces que pegues en el prompt y los productos que agregues arriba. No inventará URLs ni productos.
                      </small>
                      <button
                        type="button"
                        className="newsletter-btn newsletter-btn-primary"
                        onClick={generateNewsletterTemplateWithAi}
                        disabled={aiTemplateLoading || !aiPrompt.trim()}
                        style={{ marginTop: 'var(--spacing-sm)' }}
                      >
                        {aiTemplateLoading ? 'Generando…' : '✨ Crear plantilla con asistente de IA'}
                      </button>
                    </div>
                    {aiTemplateError && (
                      <div className="newsletter-ai-error" style={{ marginTop: 'var(--spacing-sm)', color: 'var(--color-accent-magenta)', fontSize: '0.875rem' }}>
                        {aiTemplateError}
                      </div>
                    )}
                  </div>
                  
                  <div className="newsletter-form-group">
                    <div className="newsletter-editor-toolbar">
                      <label className="newsletter-label">
                        Contenido HTML <span className="newsletter-required">*</span>
                      </label>
                      <div className="newsletter-editor-toolbar-buttons">
                        <button
                          type="button"
                          className="newsletter-btn newsletter-btn-small newsletter-btn-secondary"
                          onClick={() => setShowFullScreenEditor(true)}
                          title="Editar a pantalla completa"
                        >
                          🖵 Pantalla completa
                        </button>
                        <button
                          type="button"
                          className="newsletter-btn newsletter-btn-small newsletter-btn-secondary"
                          onClick={copyHtmlToClipboard}
                          title="Copiar el código HTML al portapapeles"
                        >
                          📋 Copiar HTML
                        </button>
                        <button
                          type="button"
                          className="newsletter-btn newsletter-btn-small newsletter-btn-secondary"
                          onClick={selectAllHtml}
                          title="Seleccionar todo el contenido del editor"
                        >
                          ☑️ Seleccionar todo
                        </button>
                      </div>
                    </div>
                    <div className="newsletter-unsubscribe-note">
                      <span className="newsletter-unsubscribe-note-badge">Footer automático</span>
                      Cada envío agrega un botón <strong>Desuscribirme</strong> en el pie del email. El enlace se firma por destinatario y lo elimina de futuras campañas.
                    </div>
                    
                    <textarea 
                      ref={htmlTextareaRef}
                      className="newsletter-textarea"
                      placeholder="Ingresa el código HTML del email"
                      value={campaignForm.html}
                      onChange={e => setCampaignForm({...campaignForm, html: e.target.value})}
                      rows={12}
                      style={{ fontFamily: 'monospace' }}
                    />
                  </div>
                  </>
                  )}

                  {/* Programación de envío */}
                  <details className="newsletter-form-group newsletter-collapse" open={!!(campaignForm.scheduledAt)}>
                    <summary className="newsletter-collapse-head">
                      <span className="newsletter-collapse-title">
                        ⏰ Programar envío <span className="newsletter-collapse-optional">(opcional)</span>
                        {campaignForm.scheduledAt && (
                          <span className="newsletter-collapse-badge">Programada</span>
                        )}
                      </span>
                      <span className="newsletter-collapse-chevron" aria-hidden="true">▾</span>
                    </summary>
                    <div className="newsletter-collapse-body">
                    <input
                      type="datetime-local"
                      className="newsletter-input"
                      value={campaignForm.scheduledAt || ''}
                      onChange={(e) => setCampaignForm({ ...campaignForm, scheduledAt: e.target.value })}
                    />
                    <small style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                      Si elegís fecha y hora, la campaña se activa sola en ese momento (el sistema revisa cada ~5 minutos). Dejalo vacío para enviar manualmente con "Activar".
                    </small>
                    </div>
                  </details>
              </div>

              <div className="newsletter-editor-preview">
                <NewsletterEmailPreview
                  html={withPreviewUnsubscribeFooter(campaignForm.html)}
                  onFullScreen={() => setShowFullScreenPreview(true)}
                  fill
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: selector de productos para plantilla con ofertas */}
      {showOfferProductPicker && (
        <div className="newsletter-modal-overlay" onClick={() => setShowOfferProductPicker(false)}>
          <div className="newsletter-modal newsletter-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="newsletter-modal-header">
              <h3>Agregar productos a la plantilla</h3>
              <button type="button" className="newsletter-modal-close" onClick={() => setShowOfferProductPicker(false)}>×</button>
            </div>
            <div className="newsletter-modal-body">
              <div className="newsletter-offer-picker-filters">
                <select
                  className="newsletter-input"
                  value={offerCategoryFilter}
                  onChange={(e) => setOfferCategoryFilter(e.target.value)}
                >
                  <option value="">Todas las categorías</option>
                  {Array.from(new Set(allOfferProducts.map((p) => (p.category || '').toString().trim()).filter(Boolean))).map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <input
                  type="text"
                  className="newsletter-input"
                  placeholder="Buscar por nombre o descripción..."
                  value={offerSearch}
                  onChange={(e) => setOfferSearch(e.target.value)}
                />
              </div>
              {offerProductsLoading ? (
                <p style={{ color: 'var(--color-text-secondary)' }}>Cargando productos…</p>
              ) : (() => {
                const cat = offerCategoryFilter.trim();
                const kw = offerSearch.trim().toLowerCase();
                const pickedIds = productPickerTarget === 'ai'
                  ? offerProductIds
                  : ((campaignForm.blocks || []).find((b) => b.id === productPickerTarget)?.items || []).map((it) => it.id);
                const handlePick = (p) => {
                  if (productPickerTarget === 'ai') addOfferProduct(p);
                  else addProductToBlock(productPickerTarget, p);
                };
                const filtered = allOfferProducts.filter((p) => {
                  if (cat && (p.category || '') !== cat) return false;
                  if (!kw) return true;
                  const haystack = [p.model, p.name, p.description, p.category].filter(Boolean).join(' ').toLowerCase();
                  return haystack.includes(kw);
                });
                if (filtered.length === 0) {
                  return <p style={{ color: 'var(--color-text-secondary)' }}>No hay productos que coincidan.</p>;
                }
                return (
                  <div className="newsletter-offer-picker-grid">
                    {filtered.map((p) => (
                      <div key={p.id} className="newsletter-offer-picker-card">
                        {p.displayImageUrl ? (
                          <img src={p.displayImageUrl} alt={p.model || p.name || ''} className="newsletter-offer-picker-img" />
                        ) : (
                          <div className="newsletter-offer-picker-noimg">Sin imagen</div>
                        )}
                        <div className="newsletter-offer-picker-name">{p.model || p.name || 'Producto'}</div>
                        <button
                          type="button"
                          className="newsletter-btn newsletter-btn-small newsletter-btn-secondary"
                          onClick={() => handlePick(p)}
                          disabled={pickedIds.includes(p.id)}
                        >
                          {pickedIds.includes(p.id) ? 'Agregado' : 'Agregar'}
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {showFullScreenEditor && (
        <div className="newsletter-fullscreen-overlay" onClick={() => setShowFullScreenEditor(false)}>
          <div className="newsletter-fullscreen-content" onClick={(e) => e.stopPropagation()}>
            <div className="newsletter-fullscreen-header">
              <span>Editor HTML</span>
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                <button
                  className="newsletter-btn newsletter-btn-secondary newsletter-btn-small"
                  onClick={() => setShowFullScreenEditor(false)}
                >
                  Cerrar
                </button>
              </div>
            </div>
            <textarea
              className="newsletter-fullscreen-editor"
              value={campaignForm.html}
              onChange={e => setCampaignForm({ ...campaignForm, html: e.target.value })}
              placeholder="Ingresa el código HTML del email"
            />
          </div>
        </div>
      )}

      {showFullScreenPreview && (
        <div className="newsletter-fullscreen-overlay" onClick={() => setShowFullScreenPreview(false)}>
          <div className="newsletter-fullscreen-content newsletter-fullscreen-preview" onClick={(e) => e.stopPropagation()}>
            <div className="newsletter-fullscreen-header">
              <span>Vista Previa del Email</span>
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                <button
                  className="newsletter-btn newsletter-btn-secondary newsletter-btn-small"
                  onClick={() => setShowFullScreenPreview(false)}
                >
                  Cerrar
                </button>
              </div>
            </div>
            <NewsletterEmailPreview
              html={withPreviewUnsubscribeFooter(campaignForm.html)}
              height={window.innerHeight - 140}
            />
          </div>
        </div>
      )}

      {/* Modal de Confirmación */}
      {confirmModal.show && (
        <div className="newsletter-modal-overlay" onClick={handleCancel}>
          <div className="newsletter-modal" onClick={e => e.stopPropagation()}>
            <div className="newsletter-modal-header">
              <h3>{confirmModal.title}</h3>
              <button 
                className="newsletter-modal-close" 
                onClick={handleCancel}
              >
                ×
              </button>
            </div>
            
            <div className="newsletter-modal-body">
              <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                {confirmModal.message}
              </p>
            </div>
            
            <div className="newsletter-modal-footer">
              <button 
                className="newsletter-btn newsletter-btn-secondary" 
                onClick={handleCancel}
              >
                Cancelar
              </button>
              <button 
                className="newsletter-btn newsletter-btn-danger"
                onClick={handleConfirm}
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cómo funciona */}
      {showHelpModal && (
        <div className="newsletter-modal-overlay" onClick={() => setShowHelpModal(false)}>
          <div className="newsletter-modal newsletter-modal-large newsletter-modal-help" onClick={e => e.stopPropagation()}>
            <div className="newsletter-modal-header">
              <h3>Guía: flujo de trabajo de envíos masivos</h3>
              <button 
                className="newsletter-modal-close" 
                onClick={() => setShowHelpModal(false)}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div className="newsletter-modal-body newsletter-help-body">
              <section className="newsletter-help-section">
                <h4>¿Qué hace esta sección?</h4>
                <p>Permite crear <strong>campañas de email</strong>, elegir a quiénes enviar (lista de suscriptores y/o grupos de contactos) y controlar el envío con un solo botón: <strong>Activar</strong> enciende la campaña y comienza el envío automático; <strong>Pausar</strong> lo detiene. Cuando todos los emails se han enviado, la campaña se marca como <strong>Finalizada</strong> automáticamente.</p>
              </section>

              <section className="newsletter-help-section">
                <h4>1. Grupos</h4>
                <p>Los <strong>grupos</strong> son listas de correos que vos definís (por ejemplo: "Clientes VIP", "Gimnasios"). Cada grupo tiene un nombre y una lista de emails (uno por línea o separados por coma). Podés crear, editar y eliminar grupos. Sirven para reutilizar los mismos contactos en varias campañas.</p>
              </section>

              <section className="newsletter-help-section">
                <h4>2. Grupos automáticos (AUTO)</h4>
                <p>Hay dos grupos especiales marcados como <strong>AUTO</strong> que el sistema genera por vos:</p>
                <ul>
                  <li><strong>Suscriptores:</strong> se arma con los contactos que se anotaron en tu web (colección <strong>Subscriptions</strong>). Sincronizalo con <strong>🔄 Sincronizar suscriptores</strong>.</li>
                  <li><strong>Base de clientes:</strong> consolida los emails únicos de <strong>ventas físicas</strong>, <strong>pedidos web</strong> y <strong>Mercado Pago</strong>. Sincronizalo con <strong>🔄 Sincronizar base de clientes</strong>.</li>
                </ul>
                <p>Los grupos AUTO no se editan manualmente (solo se sincronizan) y, una vez sincronizados, funcionan igual que cualquier otro grupo: los marcás como destinatario de una campaña y quedan congelados durante el envío. Para incluir contactos nuevos, volvé a sincronizar antes de crear la campaña.</p>
              </section>

              <section className="newsletter-help-section">
                <h4>3. Crear una campaña</h4>
                <p>En <strong>Campañas</strong> → “Nueva Campaña” definís:</p>
                <ul>
                  <li><strong>Nombre:</strong> para identificar la campaña. Este mismo título se usa como <strong>asunto del email</strong> que reciben los destinatarios.</li>
                  <li><strong>Destinatarios:</strong> marcá uno o más grupos (manuales o AUTO). La campaña enviará a la unión de todos los seleccionados (sin duplicados).</li>
                  <li><strong>Contenido HTML:</strong> el cuerpo del email en HTML. Tenés vista previa, copiar HTML y seleccionar todo.</li>
                </ul>
                <p>Al guardar, la campaña queda <strong>pausada</strong>. Un solo clic en <strong>Activar</strong> la enciende y comienza el envío automático.</p>
              </section>

              <section className="newsletter-help-section">
                <h4>4. Estados de una campaña</h4>
                <ul>
                  <li><strong>Pausada:</strong> no se envía ningún email.</li>
                  <li><strong>Activa:</strong> la campaña está encendida y el envío automático está en marcha (p. ej. 1 email cada 3 min, según el servidor).</li>
                  <li><strong>Finalizada:</strong> cuando se llegó al 100% de envíos, la campaña se marca sola como finalizada y se detiene el envío.</li>
                </ul>
              </section>

              <section className="newsletter-help-section">
                <h4>5. Controles en cada fila de campaña</h4>
                <ul>
                  <li><strong>Editar (lápiz):</strong> cambiar nombre, destinatarios o HTML.</li>
                  <li><strong>Pausar / Activar:</strong> un clic en Activar (▶️) enciende la campaña y comienza el envío automático; Pausar (⏸️) lo detiene.</li>
                  <li><strong>Eliminar (papelera):</strong> borrar la campaña (irreversible).</li>
                  <li><strong>Checkbox (cuadro):</strong> marcar para acciones masivas (p. ej. eliminar varias a la vez).</li>
                </ul>
              </section>

              <section className="newsletter-help-section">
                <h4>6. Acciones masivas</h4>
                <p>Marcá el checkbox “Seleccionar” en las campañas que quieras. Arriba aparecerá “N seleccionada(s)” con el botón <strong>Eliminar</strong> para borrarlas todas (con confirmación). “Desmarcar” limpia la selección. “Seleccionar todas” marca todas las campañas de la lista.</p>
              </section>

              <section className="newsletter-help-section">
                <h4>7. Progreso y finalización</h4>
                <p>En cada campaña se muestra <strong>Progreso: X / Y</strong> (emails enviados / total de destinatarios). El envío real lo hace un proceso en el servidor (por ejemplo una tarea programada) que va agregando cada email enviado a la campaña. Cuando <strong>X = Y</strong> (100%), la app marca la campaña como <strong>Finalizada</strong>, la desactiva y deja de enviar.</p>
              </section>

              <section className="newsletter-help-section">
                <h4>8. Analíticas</h4>
                <p>En la pestaña <strong>Analíticas</strong> ves un resumen: cantidad de campañas, activas y el progreso de cada una. El “Resumen” del lateral indica si hay campañas activas (sistema activo) o no.</p>
              </section>
            </div>
            <div className="newsletter-modal-footer">
              <button 
                className="newsletter-btn newsletter-btn-primary"
                onClick={() => setShowHelpModal(false)}
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notificaciones */}
      <Notification
        message={notification.message}
        show={notification.show}
        type={notification.type}
        onClose={hideNotification}
      />
    </div>
  );
};

export default NewsletterManager;